import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { sanitizeHtml } from '../utils/sanitizeHtml';
import {
    normalizeFrame,
    frameForBlock,
    boundingBoxOf,
    applyResizeHandle,
    scaleFrameWithinBox,
    MIN_TEXT_SCALE,
    MAX_TEXT_SCALE,
    EXPORT_SLIDE_W,
    EXPORT_CONTENT_H
} from '../utils/slideFrames.js';
import {
    emptyGuides,
    loadGuides,
    saveGuides,
    loadRulerPref,
    sanitizeGuides,
    snapFrameToGuides,
    tidyGuidePosition
} from '../utils/slideGuides.js';
import { detectQuotes, quoteToBlockData, removeQuotesAt } from '../utils/quoteDetection.js';
import { writeBlockClipboard, readBlockClipboard, prepareBlocksForPaste } from '../utils/blockClipboard.js';
import {
    imageFilesFromClipboard,
    nameClipboardImage,
    frameForPastedImage,
    fileToDataUrl,
    measureImage
} from '../utils/clipboardImages.js';
import {
    inlineStyleBlocks,
    mergeFormatting,
    htmlToPlainText,
    plainTextToHtml
} from '../utils/pasteFormatting.js';
import { hasPlaylistCitationDetails, playlistPageUrl } from '../utils/youtubePlaylist.js';
import { FONT_FAMILIES, FONT_SIZES, LINE_HEIGHTS } from '../utils/typographyOptions';

// Throwaway markers used to remember where a paste went, so the formatting
// choice offered afterwards can put the other version in the same place.
// Removed within the same tick they are written.
const PASTE_MARKER_ID = 'slide-paste-caret';
const PASTE_END_ID = 'slide-paste-end';
const PASTE_FIRST_ID = 'slide-paste-first';

// Elements a pasted run can begin with that carry paragraph spacing of their
// own, and so can hand it to the paragraph they merge into.
const PASTE_BLOCK_TAGS = new Set([
    'P', 'DIV', 'LI', 'BLOCKQUOTE', 'PRE',
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6'
]);

// Major/minor tick marks drawn along each ruler, in percent of the slide.
const MAJOR_TICKS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
const MINOR_TICKS = Array.from({ length: 51 }, (_, i) => i * 2).filter((p) => p % 10 !== 0);

// Block types whose content scales uniformly with the frame (like objects
// in PowerPoint) instead of flowing/cropping like text does
const SCALED_MEDIA_TYPES = new Set(['image', 'video', 'youtube-playlist', 'gallery', 'embedded-html', 'project-timeline']);

// Block types that keep their body as rich-text HTML on `block.content`, so
// they can be typed straight into on the canvas (double-click) instead of
// having to round-trip through the full editor modal.
const INLINE_TEXT_TYPES = new Set(['text', 'heading', 'list', 'table', 'headline']);

// Short human label shown on a selected object's tab handle.
const BLOCK_TAB_LABELS = {
    text: 'Text',
    heading: 'Heading',
    headline: 'Title',
    list: 'List',
    table: 'Table',
    image: 'Image',
    video: 'Video',
    'youtube-playlist': 'Playlist',
    audio: 'Audio',
    gallery: 'Gallery',
    link: 'Link',
    divider: 'Divider',
    html: 'Code',
    'embedded-html': 'Embed',
    'custom-html': 'HTML/CSS',
    'info-box': 'Info',
    'exercise-box': 'Exercise',
    'warning-box': 'Warning',
    'apa-quote': 'Quote',
    'apa-citation': 'Citation',
    'apa-reference-list': 'References',
    'project-timeline': 'Timeline',
    accordion: 'Accordion',
    cards: 'Cards',
    'multi-column-cards': 'Cards',
    'stacked-cards': 'Cards',
    worksheet: 'Worksheet',
    image_drawover: 'Drawing'
};

const tabLabel = (block) => BLOCK_TAB_LABELS[block?.type]
    || (block?.type ? String(block.type).replace(/[-_]/g, ' ') : 'Block');

// The rich-text wrapper class each inline-editable type renders with, so the
// editable surface inherits exactly the typography of the rendered block.
const inlineEditorClass = (type) => (type === 'headline' ? 'headline-preview' : 'rich-editor-content');

/* --- Inline ribbon vocabulary ------------------------------------------- */

// The ladder A↑/A↓ step through, in points. Taken from the shared typography
// list so growing text always lands on a size the Size menu also offers.
const FONT_SIZE_LADDER = FONT_SIZES.filter(Boolean).map((size) => parseFloat(size));

// CSS resolves pt against a fixed 96dpi, and the canvas is laid out at a
// constant pixel width and then transformed (see STAGE_WIDTH) — so a computed
// px size converts to the point size an author set, or would set, without
// depending on how large the slide happens to be drawn on screen.
const PX_PER_PT = 96 / 72;

// One press of the indent buttons, in px. 48px = 36pt = the half-inch
// PowerPoint and Word both step by.
const INDENT_STEP = 48;

// Curated palettes for the two colour menus. Small on purpose: the ribbon
// floats over the slide, and a full colour wheel there would cover the text
// being coloured. The modal editor keeps the unrestricted picker.
const TEXT_SWATCHES = [
    '#000000', '#374151', '#6b7280', '#9ca3af', '#e5e7eb', '#ffffff',
    '#b91c1c', '#ea580c', '#ca8a04', '#15803d', '#1d4ed8', '#7c3aed'
];

const HIGHLIGHT_SWATCHES = [
    '#fef08a', '#bbf7d0', '#bfdbfe', '#fbcfe8', '#fed7aa', '#e9d5ff'
];

/**
 * Ribbon glyphs that text characters cannot carry honestly — the alignment and
 * indent commands, whose meaning is the *shape* of the lines. Everything else
 * on the ribbon (B, I, U, bullets, …) reads fine as a character.
 */
const RIBBON_ICONS = {
    alignLeft: { lines: ['M2 3.5h12', 'M2 6.8h8', 'M2 10.1h12', 'M2 13.4h8'] },
    alignCenter: { lines: ['M2 3.5h12', 'M4 6.8h8', 'M2 10.1h12', 'M4 13.4h8'] },
    alignRight: { lines: ['M2 3.5h12', 'M6 6.8h8', 'M2 10.1h12', 'M6 13.4h8'] },
    indent: { lines: ['M2 3.5h12', 'M7 6.8h7', 'M7 10.1h7', 'M2 13.4h12'], arrow: 'M1.5 6.6l3 1.9-3 1.9z' },
    outdent: { lines: ['M2 3.5h12', 'M7 6.8h7', 'M7 10.1h7', 'M2 13.4h12'], arrow: 'M4.5 6.6l-3 1.9 3 1.9z' },
    highlighter: { lines: ['M9.4 3.4l3.2 3.2-5.6 5.6-3.2-3.2z'], arrow: 'M3.2 9.8L2 13.6l3.8-1.2z' }
};

const RibbonIcon = ({ name }) => {
    const icon = RIBBON_ICONS[name];
    if (!icon) return null;
    return (
        <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
            {icon.lines.map((d) => (
                <path
                    key={d}
                    d={d}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            ))}
            {icon.arrow && <path d={icon.arrow} fill="currentColor" />}
        </svg>
    );
};

/** Computed `font-size` (always px) as a whole number of points. */
const pointsFromComputed = (fontSize) => {
    const px = parseFloat(fontSize);
    return Number.isFinite(px) && px > 0 ? Math.round(px / PX_PER_PT) : null;
};

/** The next size up (direction 1) or down (-1) from `points` on the ladder. */
const nextLadderSize = (points, direction) => {
    const from = Number.isFinite(points) ? points : 14;
    if (direction > 0) return FONT_SIZE_LADDER.find((size) => size > from) ?? FONT_SIZE_LADDER[FONT_SIZE_LADDER.length - 1];
    const smaller = FONT_SIZE_LADDER.filter((size) => size < from);
    return smaller.length ? smaller[smaller.length - 1] : FONT_SIZE_LADDER[0];
};

// Computed font-family is the whole stack, re-quoted by the browser. Comparing
// the first family, unquoted and lowercased, is what tells "this is Georgia"
// from "this is the theme default" without depending on that re-quoting.
const firstFamily = (stack) => String(stack || '')
    .split(',')[0]
    .replace(/["']/g, '')
    .trim()
    .toLowerCase();

const matchFontFamily = (stack) => {
    const wanted = firstFamily(stack);
    if (!wanted) return '';
    const hit = FONT_FAMILIES.find((font) => font.value && firstFamily(font.value) === wanted);
    return hit ? hit.value : '';
};

/**
 * The canvas is laid out at one fixed size and scaled to fit, PowerPoint-style.
 *
 * It used to be a fluid box — width:100% at a 13.33:6.65 ratio — with blocks
 * positioned in percentages. Positions therefore tracked the slide, but what was
 * *inside* a block did not: the canvas text scale is in `cqw`, while the card,
 * citation and timeline renderers size themselves with Tailwind's rem-based
 * utilities and fixed px padding. So as the slide grew, some type scaled with it
 * and some stayed the size it had always been. The relative proportions changed,
 * text re-wrapped inside boxes that had grown around it, and the layout appeared
 * to shift between a windowed slide and a full-screen one.
 *
 * Laying out at a constant size and applying a single transform makes the unit
 * question moot: px, rem, em and cqw all scale by the same factor because the
 * whole stage does. What you arrange at one size is what you get at every size.
 *
 * The pointer maths is unaffected. Every drag, resize, marquee and guide handler
 * derives percentages from `stageRef.getBoundingClientRect()`, and that reports
 * the *transformed* box — so a click at 40% of the visible width is still 40% of
 * the stage, whatever the scale.
 */
const CSS_PX_PER_IN = 96;
export const STAGE_WIDTH = Math.round(EXPORT_SLIDE_W * CSS_PX_PER_IN);
export const STAGE_HEIGHT = Math.round(EXPORT_CONTENT_H * CSS_PX_PER_IN);

// Corner resizing uses PowerPoint's "shrink text on overflow" behaviour. The
// frame may first contract through its unused whitespace; scaling begins only
// after it reaches the measured text bounds. Making the frame larger never
// increases the chosen font size.
export const textScaleForCornerResize = (startFrame, resizedFrame, textBounds = startFrame) => {
    const setScale = startFrame.s || 1;
    const thresholdWidth = Math.min(startFrame.w, Math.max(0, textBounds.w || 0));
    const thresholdHeight = Math.min(startFrame.h, Math.max(0, textBounds.h || 0));
    const widthRatio = thresholdWidth > 0
        ? Math.min(1, Math.max(0, resizedFrame.w) / thresholdWidth)
        : 1;
    const heightRatio = thresholdHeight > 0
        ? Math.min(1, Math.max(0, resizedFrame.h) / thresholdHeight)
        : 1;
    const shrinkRatio = Math.min(1, widthRatio, heightRatio);
    return Math.max(MIN_TEXT_SCALE, setScale * shrinkRatio);
};

// Capture the visible text's union rectangle at the start of a resize. A DOM
// Range ignores the content wrapper's width:100%/height:100%, which is exactly
// the otherwise-empty space the selection box must be allowed to shed first.
const measureTextBounds = (item, stageRect) => {
    const content = item?.querySelector?.('.slide-designer__content');
    if (!content || !stageRect?.width || !stageRect?.height || typeof document.createRange !== 'function') {
        return null;
    }
    const range = document.createRange();
    range.selectNodeContents(content);
    const rect = range.getBoundingClientRect();
    range.detach?.();
    if (!rect.width || !rect.height) return null;
    return {
        w: (rect.width / stageRect.width) * 100,
        h: (rect.height / stageRect.height) * 100
    };
};

const useStageScale = (frameRef) => {
    const [scale, setScale] = useState(null);

    useEffect(() => {
        const frame = frameRef.current;
        if (!frame) return undefined;
        const measure = () => {
            const width = frame.clientWidth;
            if (width > 0) setScale(width / STAGE_WIDTH);
        };
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(frame);
        return () => observer.disconnect();
    }, [frameRef]);

    return scale;
};

/**
 * In-place rich-text editing for a canvas text object. The block's HTML is
 * loaded into a contentEditable surface sized by the frame, so what you type is
 * laid out (and text-scaled) exactly as it will render — no modal round trip.
 * A compact ribbon floats beside the box with the common formatting commands.
 *
 * Editing ends on Escape, on a click outside the box, or via the ribbon's ✓,
 * and always commits what is in the box (nothing typed is thrown away).
 */
const InlineTextEditor = ({ block, ribbonBelow, ribbonRight, caretPoint, onCommit, onOpenModal }) => {
    const editorRef = useRef(null);
    // Latest HTML, kept in a ref so the unmount/outside-click paths can commit
    // without re-reading a detached node.
    const htmlRef = useRef(block.content || '');
    const commitRef = useRef(onCommit);
    useEffect(() => { commitRef.current = onCommit; }, [onCommit]);

    // Seed the editable surface once — React must not re-render its children or
    // the caret would jump on every keystroke.
    useEffect(() => {
        const el = editorRef.current;
        if (!el) return;
        // Seeding a contentEditable is a live-DOM write, so the stored block
        // content goes through the sanitizer here too.
        el.innerHTML = sanitizeHtml(block.content);
        el.focus();
        // Drop the caret where the user double-clicked when the browser can tell
        // us; otherwise park it at the end of the content.
        const selection = window.getSelection?.();
        let range = null;
        if (caretPoint && typeof document.caretRangeFromPoint === 'function') {
            range = document.caretRangeFromPoint(caretPoint.x, caretPoint.y);
        }
        if (!range && typeof document.createRange === 'function') {
            range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
        }
        if (range && selection) {
            selection.removeAllRanges();
            selection.addRange(range);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Clicking anywhere outside the box (or its ribbon) finishes the edit.
    useEffect(() => {
        const onDocPointerDown = (event) => {
            const target = event.target;
            if (target?.closest?.('.slide-designer__inline-editor, .slide-designer__ribbon, .slide-designer__pasteopts')) return;
            // The click itself decides where focus lands, so don't pull it back
            // to the canvas.
            commitRef.current?.(htmlRef.current, { refocusStage: false });
        };
        document.addEventListener('pointerdown', onDocPointerDown, true);
        return () => document.removeEventListener('pointerdown', onDocPointerDown, true);
    }, []);

    const syncHtml = useCallback(() => {
        if (editorRef.current) htmlRef.current = editorRef.current.innerHTML;
    }, []);

    /**
     * The words the ribbon should act on.
     *
     * The buttons suppress mousedown so the caret never leaves the text, but a
     * <select> cannot: opening it moves focus, and a focused form control is
     * enough for some engines to drop the contentEditable's range. Losing it
     * silently would be the worst outcome — a font choice would land on the
     * whole box instead of the three words that were highlighted — so the last
     * range seen inside the editor is kept as a fallback.
     */
    const savedRangeRef = useRef(null);

    const currentRange = useCallback(() => {
        const el = editorRef.current;
        if (!el) return null;
        const selection = window.getSelection?.();
        const live = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
        if (live && el.contains(live.commonAncestorContainer)) return live;
        const saved = savedRangeRef.current;
        return saved && el.contains(saved.commonAncestorContainer) ? saved : null;
    }, []);

    /**
     * The nearest non-inline ancestor of a node, stopping at the editing host.
     *
     * Used instead of reading the host's direct children so that text nested
     * inside a <strong> or a list item resolves to the paragraph or <li> that
     * actually owns it, rather than to whichever wrapper happens to be
     * outermost.
     */
    const blockAncestor = useCallback((node) => {
        const el = editorRef.current;
        let current = node?.nodeType === Node.TEXT_NODE ? node.parentElement : node;
        while (current && current !== el) {
            if (getComputedStyle(current).display !== 'inline') return current;
            current = current.parentElement;
        }
        return null;
    }, []);

    // document.execCommand is deprecated but is still the only cross-browser way
    // to format a plain contentEditable surface, and it keeps this lightweight
    // editor free of a second editor engine on the canvas.
    const exec = useCallback((command, value = null) => {
        editorRef.current?.focus();
        try {
            document.execCommand(command, false, value);
        } catch {
            /* command unsupported in this browser — ignore */
        }
        syncHtml();
    }, [syncHtml]);

    const applyBlockFormat = useCallback((tag) => exec('formatBlock', tag), [exec]);

    /**
     * What the caret is sitting in, so the ribbon can show it.
     *
     * A menu that always reads "Font" tells an author nothing about the text
     * they are looking at, and a B button that never lights up leaves them
     * guessing whether the click landed. Everything here is read from the
     * computed style at the selection start rather than from queryCommandState:
     * the styles this ribbon writes are spans, not execCommand's own markup, so
     * the computed value is the only thing that sees both.
     */
    const [format, setFormat] = useState({});

    // The formatting choice offered right after a paste, Word-style. Null when
    // there is no recent paste to reconsider. Mirrored in a ref so the keydown
    // handler can check it without being rebuilt on every paste.
    const [pasteOptions, setPasteOptions] = useState(null);
    const pasteOptionsRef = useRef(null);
    useEffect(() => { pasteOptionsRef.current = pasteOptions; }, [pasteOptions]);

    // Which colour menu is open, and the colour each of its buttons advertises.
    // The last pick is remembered for the life of the edit so the swatch under
    // the glyph keeps meaning "this is what I will apply".
    const [openMenu, setOpenMenu] = useState(null);
    const [lastTextColor, setLastTextColor] = useState(TEXT_SWATCHES[6]);
    const [lastHighlight, setLastHighlight] = useState(HIGHLIGHT_SWATCHES[0]);

    const readFormat = useCallback(() => {
        const el = editorRef.current;
        if (!el) return;
        const range = currentRange();
        let node = null;
        if (range) {
            /**
             * The element whose styling the selection actually shows.
             *
             * Reading the range's own start container is not enough. Selecting
             * a whole line — triple-click, or Ctrl+A — gives a container of the
             * *paragraph* with an offset, so the ribbon would report the
             * paragraph's font while the author is looking at a 24pt heading
             * span inside it, and the menus would sit there insisting the text
             * is something it plainly isn't. Descending to the first text run
             * the selection covers is what makes the ribbon track the words on
             * screen.
             */
            let start = range.startContainer;
            if (start?.nodeType === Node.ELEMENT_NODE) {
                const child = start.childNodes[range.startOffset] || start.firstChild;
                let deepest = child;
                while (deepest?.firstChild) deepest = deepest.firstChild;
                start = deepest || start;
            }
            node = start?.nodeType === Node.TEXT_NODE ? start.parentElement : start;
        }
        if (!node || !el.contains(node)) node = el;

        let computed = null;
        try { computed = getComputedStyle(node); } catch { /* detached node */ }
        if (!computed) return;

        const block = blockAncestor(node);
        const align = computed.textAlign === 'start' || computed.textAlign === '' ? 'left' : computed.textAlign;
        const next = {
            fontFamily: matchFontFamily(computed.fontFamily),
            fontSize: pointsFromComputed(computed.fontSize),
            bold: parseInt(computed.fontWeight, 10) >= 600 || computed.fontWeight === 'bold',
            italic: computed.fontStyle === 'italic',
            underline: (computed.textDecorationLine || computed.textDecoration || '').includes('underline'),
            strike: (computed.textDecorationLine || computed.textDecoration || '').includes('line-through'),
            align,
            blockTag: (block?.tagName || '').toLowerCase()
        };
        // selectionchange fires on every caret move; re-rendering the ribbon
        // only when something it displays actually changed keeps typing cheap.
        setFormat((prev) => (
            Object.keys(next).every((key) => prev[key] === next[key]) ? prev : next
        ));
    }, [blockAncestor, currentRange]);

    useEffect(() => {
        readFormat();
        const onSelectionChange = () => {
            const el = editorRef.current;
            const selection = window.getSelection?.();
            const anchor = selection?.anchorNode;
            if (!el || !anchor || !el.contains(anchor)) return;
            if (selection.rangeCount > 0) savedRangeRef.current = selection.getRangeAt(0).cloneRange();
            readFormat();
        };
        document.addEventListener('selectionchange', onSelectionChange);
        return () => document.removeEventListener('selectionchange', onSelectionChange);
    }, [readFormat]);

    // Every command runs through here so the ribbon's own state refreshes even
    // when the command did not move the caret (bold on a selection, say).
    const run = useCallback((action) => {
        action();
        readFormat();
    }, [readFormat]);

    /**
     * The text nodes the current selection actually covers.
     *
     * Both stylers work from text nodes rather than from the range as a whole.
     * Wrapping a whole range in one element is the obvious approach and it is
     * wrong here: a selection spanning two paragraphs would put a <span> around
     * <p> elements, which is invalid nesting — the browser and the sanitizer
     * then rearrange it and the styling lands somewhere nobody asked for.
     */
    const selectedTextNodes = useCallback((range) => {
        const el = editorRef.current;
        if (!el) return [];
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
            acceptNode: (node) => (
                node.nodeValue
                && node.nodeValue.trim()
                && range.intersectsNode(node)
                    ? NodeFilter.FILTER_ACCEPT
                    : NodeFilter.FILTER_REJECT
            )
        });
        const nodes = [];
        while (walker.nextNode()) nodes.push(walker.currentNode);
        return nodes;
    }, []);

    /**
     * Apply one CSS property to the selected words.
     *
     * execCommand has `fontName` and `fontSize`, but neither is usable: fontName
     * emits a <font> element, which the slide sanitizer drops on the way to the
     * rendered slide, and fontSize only accepts the legacy 1–7 scale rather than
     * the point sizes the modal editor offers. Wrapping each covered text run in
     * a styled span produces exactly the markup the modal produces, so text
     * styled on the canvas and text styled in the modal are indistinguishable.
     *
     * `value` may be a function of the run's current parent element, for the
     * commands whose new value depends on the old one — A↑/A↓ step each run up
     * or down from whatever size that run already had, the way PowerPoint's do.
     *
     * With nothing selected the whole text box is styled. The alternative — do
     * nothing, which is what this used to do — reads as a broken menu, and
     * "applies to every word in the box" is what PowerPoint does when the shape
     * rather than a word is what you have in hand.
     */
    const styleSelection = useCallback((property, value) => {
        const el = editorRef.current;
        const selection = window.getSelection?.();
        if (!el || !selection) return;

        const live = currentRange();
        const wholeBox = !live || live.collapsed;
        // Where the caret was, so the whole-box path can put it back rather
        // than leaving every word in the box selected.
        const caret = wholeBox && live ? { node: live.startContainer, offset: live.startOffset } : null;

        let range = live;
        if (wholeBox) {
            range = document.createRange();
            range.selectNodeContents(el);
        }

        // Boundaries are captured before any splitting, since splitText on the
        // start node would otherwise move the range out from under us.
        const { startContainer, startOffset, endContainer, endOffset } = range;
        const nodes = selectedTextNodes(range);
        if (nodes.length === 0) return;

        const wrapped = [];
        nodes.forEach((node) => {
            let target = node;
            // Trim to the selection at each end, so styling half a word styles
            // only that half. End first: splitting the start would shift the
            // end offset when both ends are in the same text node.
            if (target === endContainer && endOffset < target.nodeValue.length) {
                target.splitText(endOffset);
            }
            if (target === startContainer && startOffset > 0) {
                target = target.splitText(startOffset);
            }
            const span = document.createElement('span');
            if (typeof property === 'string') {
                const resolved = typeof value === 'function' ? value(target.parentElement) : value;
                if (resolved === null || resolved === undefined) return;
                span.style[property] = resolved;
            } else {
                // A whole set of properties at once, for the format painter:
                // seven separate calls would leave seven nested spans around
                // every word it touched.
                let applied = false;
                Object.entries(property || {}).forEach(([name, val]) => {
                    if (val) { span.style[name] = val; applied = true; }
                });
                if (!applied) return;
            }
            target.parentNode.insertBefore(span, target);
            span.appendChild(target);
            wrapped.push(span);
        });

        if (wrapped.length === 0) return;

        selection.removeAllRanges();
        const next = document.createRange();
        if (caret && el.contains(caret.node)) {
            // Re-parenting a text node into a span leaves the node itself
            // intact, so the caret's own offset is still meaningful.
            next.setStart(caret.node, Math.min(caret.offset, caret.node.length ?? caret.offset));
            next.collapse(true);
        } else {
            // Keep the words selected, so a second choice — size after font,
            // say — lands on the same text instead of on nothing.
            next.setStartBefore(wrapped[0]);
            next.setEndAfter(wrapped[wrapped.length - 1]);
        }
        selection.addRange(next);
        savedRangeRef.current = next.cloneRange();
        syncHtml();
    }, [currentRange, selectedTextNodes, syncHtml]);

    /**
     * Set a property on every block the selection touches.
     *
     * Alignment, line spacing and indent belong to the paragraph, not to a run
     * of words, so they go on blocks rather than on a wrapping span. With the
     * caret parked rather than a range selected, that is the one paragraph the
     * caret sits in — the same thing PowerPoint aligns or indents.
     *
     * `value` may be a function of the block, for the commands that step from
     * the block's current value (indent in/out).
     *
     * When the surface holds bare text with no block element at all, one is
     * created first: otherwise the style would land on the contentEditable host,
     * which is not part of the committed HTML and would vanish on save.
     */
    const styleBlocks = useCallback((property, value) => {
        const el = editorRef.current;
        if (!el) return;

        if (!el.querySelector(':scope > *')) {
            try {
                document.execCommand('formatBlock', false, 'p');
            } catch { /* older engine — the fallback below still applies */ }
        }

        const range = currentRange();
        if (!range) return;
        const blocks = new Set();
        if (range.collapsed) {
            const block = blockAncestor(range.startContainer);
            if (block) blocks.add(block);
        } else {
            selectedTextNodes(range).forEach((node) => {
                const block = blockAncestor(node);
                // Text sitting directly in the host with no block wrapper: fall
                // back to the host's own children so something real gets styled.
                if (block) blocks.add(block);
            });
        }

        const targets = blocks.size ? Array.from(blocks) : Array.from(el.children);
        targets.forEach((node) => {
            const resolved = typeof value === 'function' ? value(node) : value;
            if (resolved !== null && resolved !== undefined) node.style[property] = resolved;
        });
        syncHtml();
    }, [blockAncestor, currentRange, selectedTextNodes, syncHtml]);

    const applyFontFamily = useCallback((value) => {
        // 'Default' hands the text back to the slide theme rather than pinning
        // it to whatever the theme currently happens to be.
        styleSelection('fontFamily', value || 'inherit');
    }, [styleSelection]);

    const applyFontSize = useCallback((value) => {
        styleSelection('fontSize', value || 'inherit');
    }, [styleSelection]);

    const applyLineHeight = useCallback((value) => {
        if (value) styleBlocks('lineHeight', value);
    }, [styleBlocks]);

    const applyAlign = useCallback((value) => {
        styleBlocks('textAlign', value);
    }, [styleBlocks]);

    /**
     * Grow/shrink each selected run one step along the point ladder.
     *
     * Per-run rather than one size for the whole selection: text at 12pt and
     * text at 24pt in the same selection both get bigger, and stay different
     * sizes. Flattening them to a single size is the thing that makes A↑ feel
     * destructive.
     */
    const stepFontSize = useCallback((direction) => {
        styleSelection('fontSize', (element) => {
            const current = element ? pointsFromComputed(getComputedStyle(element).fontSize) : null;
            return `${nextLadderSize(current, direction)}pt`;
        });
    }, [styleSelection]);

    /**
     * Indent in/out, as a left margin on the paragraph.
     *
     * execCommand('indent') is the obvious route and the wrong one: it wraps the
     * paragraph in a <blockquote>, which the slide's stylesheet renders as a
     * quote — a visual change nobody asked for — and which nests deeper on every
     * press. A margin is what the indent actually is.
     */
    const adjustIndent = useCallback((direction) => {
        styleBlocks('marginLeft', (block) => {
            const current = parseFloat(getComputedStyle(block).marginLeft) || 0;
            const next = Math.max(0, Math.round(current + (direction * INDENT_STEP)));
            return `${next}px`;
        });
    }, [styleBlocks]);

    /**
     * Format painter: pick the look up here, brush it onto text over there.
     *
     * Armed by the ribbon button, spent on the next selection made inside this
     * box — one pickup, one application, like Word's single-click painter.
     * Deliberately scoped to the block being edited: this editor's DOM is the
     * only place it can reach, and the alternative (carrying a look between
     * blocks) would need the formatting to survive the edit ending.
     */
    const [painter, setPainter] = useState(null);
    const painterRef = useRef(null);
    useEffect(() => { painterRef.current = painter; }, [painter]);

    const copyFormatting = useCallback(() => {
        if (painterRef.current) { setPainter(null); return; }
        const el = editorRef.current;
        const range = currentRange();
        let node = range ? range.startContainer : null;
        if (node?.nodeType === Node.ELEMENT_NODE) {
            let deepest = node.childNodes[range.startOffset] || node.firstChild;
            while (deepest?.firstChild) deepest = deepest.firstChild;
            node = deepest || node;
        }
        if (node?.nodeType === Node.TEXT_NODE) node = node.parentElement;
        if (!node || !el?.contains(node)) node = el;
        if (!node) return;

        let computed = null;
        try { computed = getComputedStyle(node); } catch { /* detached */ }
        if (!computed) return;

        const background = computed.backgroundColor;
        setPainter({
            fontFamily: computed.fontFamily,
            fontSize: computed.fontSize,
            fontWeight: computed.fontWeight,
            fontStyle: computed.fontStyle,
            textDecorationLine: computed.textDecorationLine === 'none' ? '' : computed.textDecorationLine,
            color: computed.color,
            // A see-through background is the absence of a highlight, not a
            // colour to paint on.
            backgroundColor: /rgba\(0,\s*0,\s*0,\s*0\)|transparent/.test(background) ? '' : background
        });
    }, [currentRange]);

    const applyPainter = useCallback(() => {
        const styles = painterRef.current;
        if (!styles) return;
        const range = currentRange();
        if (!range || range.collapsed) return;
        styleSelection(styles);
        setPainter(null);
        readFormat();
    }, [currentRange, styleSelection, readFormat]);

    const applyTextColor = useCallback((color) => styleSelection('color', color), [styleSelection]);
    const applyHighlight = useCallback((color) => styleSelection('backgroundColor', color), [styleSelection]);

    const addLink = useCallback(() => {
        const url = window.prompt('Link URL');
        if (url === null) return;
        if (url.trim() === '') exec('unlink');
        else exec('createLink', url.trim());
    }, [exec]);

    // A headline block is a single heading node in the modal editor, so Enter
    // must not split it into extra blocks that the modal would drop.
    const isHeadline = block.type === 'headline';

    const onKeyDown = useCallback((event) => {
        // Keep canvas shortcuts (delete/nudge/group) from firing while typing.
        event.stopPropagation();
        if (isHeadline && event.key === 'Enter' && !event.metaKey && !event.ctrlKey) {
            event.preventDefault();
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            // Escape retires the paste chip first, so dismissing it does not
            // also end the edit the author is in the middle of.
            if (pasteOptionsRef.current) { setPasteOptions(null); return; }
            syncHtml();
            commitRef.current?.(htmlRef.current);
            return;
        }
        // Any real typing moves on from the paste; the offer stops applying.
        if (pasteOptionsRef.current && event.key.length === 1 && !event.metaKey && !event.ctrlKey) {
            setPasteOptions(null);
        }
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            syncHtml();
            commitRef.current?.(htmlRef.current);
        }
    }, [syncHtml, isHeadline]);

    /**
     * Paste keeping the source's styling, sanitized.
     *
     * This used to force plain text, on the reasoning that a paste from the web
     * would otherwise wreck a canvas layout. That also threw away every font,
     * size and colour an author had set up elsewhere — the same loss that was
     * just fixed in the modal editor, so keeping it here would leave the two
     * surfaces disagreeing about what paste means.
     *
     * The markup goes through sanitizeHtml because this writes into a live
     * contentEditable: clipboard HTML is untrusted, and innerHTML on an attached
     * node is exactly the sink that guards against. Plain text is still the
     * fallback when the clipboard carries no HTML.
     */
    /**
     * Paste, with the choice Word gives you.
     *
     * The paste happens immediately keeping the source's formatting, and a
     * small chip appears beside it offering to strip that formatting instead.
     * That ordering is Word's, and it is the right way round: the common case
     * costs no clicks, and the author who wanted plain text has one waiting
     * rather than a dialog standing between them and their document.
     *
     * Switching works by restoring a snapshot taken at the caret and pasting
     * again the other way, so choosing is not "undo and redo it yourself" and
     * the two options are genuinely equivalent in effort.
     */
    /**
     * Give the paragraph a paste landed in the spacing of the paragraph it
     * absorbed.
     *
     * Pasting a block of text at the end of an existing paragraph merges the
     * first pasted paragraph into that paragraph — that is how a contentEditable
     * behaves, and how Word behaves. What it leaves behind is a paragraph
     * wearing the *destination's* spacing while every paragraph after it wears
     * the source's, which shows up as one stray gap between the first pasted
     * line and the second: three tight lines out of a document arrive as one
     * line, a gap, then two tight lines.
     *
     * The host is now that pasted paragraph, so it takes its spacing.
     */
    const prepareForSpacingAdoption = useCallback((html) => {
        if (!html || typeof DOMParser === 'undefined') return { html, spacing: null };

        let doc;
        try {
            doc = new DOMParser().parseFromString(html, 'text/html');
        } catch { return { html, spacing: null }; }

        const first = doc.body?.firstElementChild;
        // Tag name rather than computed display: `first` belongs to a detached
        // parse document, where the user-agent stylesheet does not resolve and
        // every element reads back as inline.
        if (!first || !PASTE_BLOCK_TAGS.has(first.tagName)) return { html, spacing: null };

        /**
         * Merging discards the first pasted paragraph's element, and with it
         * everything written on that element. For a Word paste that is the
         * whole typeface, because MsoNormal puts the font on the paragraph and
         * not on the words inside it — so the first pasted line arrived in the
         * slide's font while every line after it kept the document's.
         *
         * The character properties are therefore moved onto a span around the
         * paragraph's contents, where they travel with the words wherever the
         * merge puts them.
         */
        const character = ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle',
            'color', 'backgroundColor', 'letterSpacing', 'textDecoration',
            'textTransform', 'fontVariant']
            .reduce((acc, property) => {
                const value = first.style?.[property];
                if (value) acc[property] = value;
                return acc;
            }, {});

        if (Object.keys(character).length && first.firstChild) {
            const carrier = doc.createElement('span');
            Object.entries(character).forEach(([property, value]) => { carrier.style[property] = value; });
            while (first.firstChild) carrier.appendChild(first.firstChild);
            first.appendChild(carrier);
        }

        /**
         * Spacing stays on the paragraph, and is handed to whichever paragraph
         * ends up holding the merged content.
         *
         * Alignment is deliberately not carried over: the paragraph the paste
         * merges into usually holds text the author did not paste, and
         * re-aligning their existing words is a visible edit they did not ask
         * for. Spacing closes the gap that was reported without touching what
         * was already there.
         */
        const spacing = ['marginTop', 'marginBottom', 'lineHeight', 'textIndent']
            .reduce((acc, property) => {
                const value = first.style?.[property];
                if (value) acc[property] = value;
                return acc;
            }, {});

        if (!Object.keys(spacing).length) {
            return { html: doc.body.innerHTML, spacing: null };
        }

        /**
         * Ride along inside the first pasted paragraph so it can be found again
         * wherever the engine decides to put its contents.
         *
         * The zero-width space is load-bearing: Chrome's insertHTML strips
         * empty inline elements, so an empty marker simply vanishes and the
         * spacing lookup finds nothing. It is removed with the marker, so it
         * never reaches the saved content.
         */
        const mark = doc.createElement('span');
        mark.id = PASTE_FIRST_ID;
        mark.textContent = '​';
        first.insertBefore(mark, first.firstChild);
        return { html: doc.body.innerHTML, spacing };
    }, []);

    /**
     * @param {object|null} spacing    properties to hand to the host paragraph
     * @param {number}      hostIndex  where that paragraph sat before the insert
     *
     * Two ways of finding the paragraph, because neither is reliable on its own
     * across engines. The marker is exact when it survives; its position among
     * the editor's children is the fallback for when it does not, and holds up
     * because an insert at the caret cannot renumber the children before it.
     * Getting this wrong is invisible — the spacing simply does not apply — so
     * it is worth having both.
     */
    const adoptFirstParagraphSpacing = useCallback((spacing, hostIndex) => {
        const el = editorRef.current;
        if (!el) return;
        const mark = el.querySelector(`#${PASTE_FIRST_ID}`);

        if (spacing) {
            let host = null;
            if (mark) {
                host = mark.parentElement;
                while (host && host !== el && !PASTE_BLOCK_TAGS.has(host.tagName)) {
                    host = host.parentElement;
                }
            }
            if ((!host || host === el) && hostIndex >= 0) {
                const candidate = el.children[hostIndex];
                if (candidate && PASTE_BLOCK_TAGS.has(candidate.tagName)) host = candidate;
            }
            if (host && host !== el) {
                Object.entries(spacing).forEach(([property, value]) => {
                    if (value) host.style[property] = value;
                });
            }
        }
        mark?.remove();
    }, []);

    const insertAtMarker = useCallback((html) => {
        const el = editorRef.current;
        const marker = el?.querySelector(`#${PASTE_MARKER_ID}`);
        if (!el || !marker) return null;

        const prepared = prepareForSpacingAdoption(html);
        // Where the caret's paragraph sits among the editor's children, so it
        // can be found again by position if the marker does not survive.
        let hostIndex = -1;
        if (prepared.spacing) {
            let block = marker.parentElement;
            while (block && block.parentElement && block.parentElement !== el) block = block.parentElement;
            if (block && block.parentElement === el) hostIndex = Array.prototype.indexOf.call(el.children, block);
        }

        const range = document.createRange();
        range.selectNode(marker);
        // Removing the marker leaves the range collapsed exactly where the
        // caret was when the paste arrived.
        range.deleteContents();
        const selection = window.getSelection?.();
        selection?.removeAllRanges();
        selection?.addRange(range);

        // The trailing marker is only there to be measured — it tells us where
        // the pasted run ended so the chip can sit beside it.
        document.execCommand('insertHTML', false, `${prepared.html}<span id="${PASTE_END_ID}"></span>`);
        adoptFirstParagraphSpacing(prepared.spacing, hostIndex);
        const end = el.querySelector(`#${PASTE_END_ID}`);
        const at = end
            ? { left: end.offsetLeft - el.scrollLeft, top: (end.offsetTop + end.offsetHeight) - el.scrollTop }
            : { left: 0, top: 0 };
        end?.remove();
        syncHtml();
        return at;
    }, [syncHtml, prepareForSpacingAdoption, adoptFirstParagraphSpacing]);

    const onPaste = useCallback((event) => {
        const clipboard = event.clipboardData;
        const el = editorRef.current;
        if (!clipboard || !el || typeof document.execCommand !== 'function') return;
        // Images pasted onto a slide are the canvas's business, not the text's.
        if (imageFilesFromClipboard(clipboard).length) return;

        const html = clipboard.getData('text/html');
        const text = clipboard.getData('text/plain');
        if (!html && !text) return;
        event.preventDefault();

        // Word writes a paragraph's base font into a <style> block and only the
        // deviations inline; resolving those first is what makes "keep the
        // formatting" keep anything at all. The result is still sanitized —
        // this widens what formatting can say, not what a paste may do.
        const rich = html ? sanitizeHtml(inlineStyleBlocks(html)) : '';
        const merged = html ? sanitizeHtml(mergeFormatting(inlineStyleBlocks(html))) : '';
        const plainText = text || htmlToPlainText(html);
        const plain = plainTextToHtml(plainText);

        document.execCommand('insertHTML', false, `<span id="${PASTE_MARKER_ID}"></span>`);
        const snapshot = el.innerHTML;
        const at = insertAtMarker(rich || plain);

        // Nothing to choose between when the clipboard carried no formatting.
        if (!rich || !plain || !html) return;
        setPasteOptions({
            snapshot,
            versions: { source: rich, merge: merged, plain },
            open: false,
            left: at?.left ?? 0,
            top: at?.top ?? 0
        });
    }, [insertAtMarker]);

    /**
     * Apply one of the three versions and retire the badge.
     *
     * Choosing is a one-shot decision, as it is in Word: the menu closes and
     * the badge goes with it. Nothing stays lit up beforehand either — the
     * paste has landed with its source formatting, but that is a starting
     * point rather than an answer already given on the author's behalf.
     */
    const choosePasteMode = useCallback((mode) => {
        const el = editorRef.current;
        const current = pasteOptionsRef.current;
        setPasteOptions(null);
        if (!current || !el) return;

        // Writing to a live contentEditable, so it goes through the sanitizer
        // like every other write — the snapshot is our own already-clean
        // markup, which makes this a no-op, but the rule is that this sink is
        // never fed unsanitized.
        el.innerHTML = sanitizeHtml(current.snapshot);
        insertAtMarker(current.versions[mode] || current.versions.source);
        el.focus();
    }, [insertAtMarker]);

    /**
     * A menu that shows what the text already is.
     *
     * `value` is whatever the caret is sitting in. When that is something the
     * list does not offer — 13pt text pasted out of a document, say — it is
     * shown as an extra row rather than snapped to the nearest listed value,
     * so the menu never claims the text is something it isn't.
     */
    const picker = (key, title, options, value, onPick, extraClass = '') => {
        const known = options.some((option) => option.value === value);
        return (
            <select
                key={key}
                className={`slide-designer__ribbonselect ${extraClass}`}
                title={title}
                aria-label={title}
                // Same reason as the buttons: choosing must not steal the caret.
                onMouseDown={(e) => e.stopPropagation()}
                value={known ? value : '__mixed'}
                onChange={(e) => { if (e.target.value !== '__mixed') run(() => onPick(e.target.value)); }}
            >
                {!known && <option value="__mixed">{value || title}</option>}
                {options.map((option) => (
                    <option key={option.label} value={option.value}>{option.label}</option>
                ))}
            </select>
        );
    };

    const btn = (key, label, title, onClick, { active = false, extraClass = '' } = {}) => (
        <button
            key={key}
            type="button"
            className={`slide-designer__ribbonbtn ${active ? 'slide-designer__ribbonbtn--on' : ''} ${extraClass}`}
            title={title}
            aria-label={title}
            aria-pressed={active || undefined}
            // Never let the button steal the selection from the editor.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => run(onClick)}
        >
            {label}
        </button>
    );

    // Text colour and highlight: a swatch grid on a button, PowerPoint-style.
    // The bar under the glyph shows the colour a pick would apply, so the
    // button says what it does before it is opened.
    const colorButton = (key, glyph, title, swatches, current, onPick, clearLabel, clearValue) => (
        <span className="slide-designer__colorwrap" key={key}>
            <button
                type="button"
                className={`slide-designer__ribbonbtn ${openMenu === key ? 'slide-designer__ribbonbtn--on' : ''}`}
                title={title}
                aria-label={title}
                aria-haspopup="true"
                aria-expanded={openMenu === key}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setOpenMenu((open) => (open === key ? null : key))}
            >
                <span className="slide-designer__colorglyph">
                    {glyph}
                    <span className="slide-designer__colorbar" style={{ background: current }} />
                </span>
            </button>
            {openMenu === key && (
                <span className="slide-designer__colormenu" role="menu">
                    <span className="slide-designer__colorgrid">
                        {swatches.map((swatch) => (
                            <button
                                key={swatch}
                                type="button"
                                className="slide-designer__swatch"
                                style={{ background: swatch }}
                                title={swatch}
                                aria-label={swatch}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => { run(() => onPick(swatch)); setOpenMenu(null); }}
                            />
                        ))}
                    </span>
                    <button
                        type="button"
                        className="slide-designer__colorclear"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { run(() => onPick(clearValue)); setOpenMenu(null); }}
                    >
                        {clearLabel}
                    </button>
                </span>
            )}
        </span>
    );

    const sizeOptions = FONT_SIZES.filter(Boolean).map((size) => ({ label: size, value: size }));
    const currentSize = format.fontSize ? `${format.fontSize}pt` : '';
    // A title block is one heading node in the modal editor, so offering it
    // paragraph and list commands would produce markup the modal then drops.
    const styleOptions = isHeadline
        ? [{ label: 'Heading 1', value: 'h1' }, { label: 'Heading 2', value: 'h2' }]
        : [
            { label: 'Body text', value: 'p' },
            { label: 'Heading 1', value: 'h1' },
            { label: 'Heading 2', value: 'h2' },
            { label: 'Heading 3', value: 'h3' }
        ];
    // A list item, a table cell or a bare run has no entry here; the menu shows
    // its own label in that case rather than naming a style that isn't applied.
    const styleValue = styleOptions.some((option) => option.value === format.blockTag)
        ? format.blockTag
        : '';

    return (
        <>
            <div
                className={`slide-designer__ribbon ${ribbonBelow ? 'slide-designer__ribbon--below' : ''} ${ribbonRight ? 'slide-designer__ribbon--right' : ''}`}
                onPointerDown={(e) => e.stopPropagation()}
                // Clicking the ribbon (buttons or the space between them) must
                // never pull the caret out of the text being edited.
                onMouseDown={(e) => e.preventDefault()}
                role="toolbar"
                aria-label="Text formatting"
            >
                {/* Row one — type. Family, size and the two step buttons act on
                    the selected words, or on the whole box when nothing is
                    selected; indent acts on the paragraphs the caret touches. */}
                <div className="slide-designer__ribbonrow">
                    <span className="slide-designer__ribbongroup">
                        {picker(
                            'font',
                            'Font',
                            FONT_FAMILIES,
                            format.fontFamily || '',
                            applyFontFamily,
                            'slide-designer__ribbonselect--font'
                        )}
                        {picker('size', 'Size', sizeOptions, currentSize, applyFontSize, 'slide-designer__ribbonselect--size')}
                        {btn('grow', <span>A<sup>▲</sup></span>, 'Increase font size', () => stepFontSize(1))}
                        {btn('shrink', <span>A<sub>▼</sub></span>, 'Decrease font size', () => stepFontSize(-1))}
                    </span>
                    <span className="slide-designer__ribbongroup">
                        {btn('outdent', <RibbonIcon name="outdent" />, 'Decrease indent', () => adjustIndent(-1))}
                        {btn('indent', <RibbonIcon name="indent" />, 'Increase indent', () => adjustIndent(1))}
                    </span>
                    <span className="slide-designer__ribbongroup">
                        {picker('style', 'Style', styleOptions, styleValue, applyBlockFormat, 'slide-designer__ribbonselect--style')}
                        {!isHeadline && picker(
                            'spacing',
                            'Spacing',
                            LINE_HEIGHTS,
                            '',
                            applyLineHeight,
                            'slide-designer__ribbonselect--spacing'
                        )}
                    </span>
                </div>
                {/* Row two — the marks and the paragraph shape. */}
                <div className="slide-designer__ribbonrow">
                    <span className="slide-designer__ribbongroup">
                        {btn('bold', <b>B</b>, 'Bold (Ctrl/⌘+B)', () => exec('bold'), { active: format.bold })}
                        {btn('italic', <i>I</i>, 'Italic (Ctrl/⌘+I)', () => exec('italic'), { active: format.italic })}
                        {btn('underline', <u>U</u>, 'Underline (Ctrl/⌘+U)', () => exec('underline'), { active: format.underline })}
                        {btn('strike', <s>S</s>, 'Strikethrough', () => exec('strikeThrough'), { active: format.strike })}
                    </span>
                    <span className="slide-designer__ribbongroup">
                        {btn('left', <RibbonIcon name="alignLeft" />, 'Align left', () => applyAlign('left'), { active: format.align === 'left' })}
                        {btn('center', <RibbonIcon name="alignCenter" />, 'Align centre', () => applyAlign('center'), { active: format.align === 'center' })}
                        {btn('right', <RibbonIcon name="alignRight" />, 'Align right', () => applyAlign('right'), { active: format.align === 'right' })}
                    </span>
                    <span className="slide-designer__ribbongroup">
                        {colorButton(
                            'color',
                            'A',
                            'Font colour',
                            TEXT_SWATCHES,
                            lastTextColor,
                            (value) => { if (value !== 'inherit') setLastTextColor(value); applyTextColor(value); },
                            'Automatic',
                            'inherit'
                        )}
                        {colorButton(
                            'highlight',
                            <RibbonIcon name="highlighter" />,
                            'Text highlight colour',
                            HIGHLIGHT_SWATCHES,
                            lastHighlight,
                            (value) => { if (value !== 'transparent') setLastHighlight(value); applyHighlight(value); },
                            'No colour',
                            'transparent'
                        )}
                        {btn(
                            'painter',
                            '🖌',
                            painter
                                ? 'Format painter is loaded — select the text to paint (click again to cancel)'
                                : 'Copy this text’s formatting, then select other text in this box to apply it',
                            copyFormatting,
                            { active: Boolean(painter) }
                        )}
                        {btn('clear', '⌫', 'Clear formatting', () => exec('removeFormat'))}
                    </span>
                    {!isHeadline && (
                        <span className="slide-designer__ribbongroup">
                            {btn('ul', '•', 'Bulleted list', () => exec('insertUnorderedList'))}
                            {btn('ol', '1.', 'Numbered list', () => exec('insertOrderedList'))}
                        </span>
                    )}
                    <span className="slide-designer__ribbongroup">
                        {btn('link', '🔗', 'Add or remove a link', addLink)}
                        {btn('modal', '⤢', 'Open the full editor modal', () => { syncHtml(); onOpenModal?.(htmlRef.current); })}
                        {btn(
                            'done',
                            '✓',
                            'Finish editing (Esc)',
                            () => { syncHtml(); commitRef.current?.(htmlRef.current); },
                            { extraClass: 'slide-designer__ribbonbtn--done' }
                        )}
                    </span>
                </div>
            </div>
            <div
                ref={editorRef}
                className={`slide-designer__inline-editor ${inlineEditorClass(block.type)}`}
                contentEditable
                suppressContentEditableWarning
                role="textbox"
                aria-multiline="true"
                aria-label="Edit block text on the slide"
                spellCheck
                onInput={syncHtml}
                onBlur={syncHtml}
                onKeyDown={onKeyDown}
                onPaste={onPaste}
                onPointerDown={(e) => { e.stopPropagation(); setOpenMenu(null); setPasteOptions(null); }}
                // A loaded format painter is spent on the selection the author
                // has just finished making — the release, not the press, is
                // when there is a selection to paint.
                onMouseUp={applyPainter}
                onKeyUp={(e) => { if (e.shiftKey) applyPainter(); }}
                onDoubleClick={(e) => e.stopPropagation()}
            />
            {/* Word's paste-options badge: the paste has already happened
                keeping the source formatting, and this offers to strip it.
                Sits where the pasted run ended, and retires on Escape, on
                typing, or on a click anywhere else. */}
            {pasteOptions && (
                <div
                    className="slide-designer__pasteopts"
                    style={{ left: `${pasteOptions.left}px`, top: `${pasteOptions.top}px` }}
                    role="group"
                    aria-label="Paste formatting options"
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.preventDefault()}
                >
                    <button
                        type="button"
                        className="slide-designer__pastebadge"
                        title="Paste options (Ctrl)"
                        aria-label="Paste options"
                        aria-haspopup="menu"
                        aria-expanded={pasteOptions.open}
                        onClick={() => setPasteOptions((current) => (
                            current ? { ...current, open: !current.open } : current
                        ))}
                    >
                        <span aria-hidden="true">📋</span>
                        <span className="slide-designer__pastebadge-caret" aria-hidden="true">▾</span>
                    </button>
                    {pasteOptions.open && (
                        <div className="slide-designer__pastemenu" role="menu">
                            <div className="slide-designer__pastemenu-title">Paste Options:</div>
                            {[
                                {
                                    mode: 'source',
                                    label: 'Keep Source Formatting',
                                    glyph: '🖹',
                                    hint: 'Fonts, sizes, colours and spacing exactly as copied'
                                },
                                {
                                    mode: 'merge',
                                    label: 'Merge Formatting',
                                    glyph: '🖆',
                                    hint: 'Keep bold, italic and layout; take this slide’s typeface'
                                },
                                {
                                    mode: 'plain',
                                    label: 'Keep Text Only',
                                    glyph: 'A',
                                    hint: 'The words alone, styled by the slide'
                                }
                            ].map((option) => (
                                <button
                                    key={option.mode}
                                    type="button"
                                    role="menuitem"
                                    className="slide-designer__pasteopt"
                                    title={option.hint}
                                    onClick={() => choosePasteMode(option.mode)}
                                >
                                    <span className="slide-designer__pasteopt-glyph" aria-hidden="true">{option.glyph}</span>
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

/**
 * Renders content at the frame's width, measures its natural height, and
 * uniformly scales it down so it always fits the frame — resize the frame
 * and the media scales with it. Mirrors the PPTX export's contain-fit.
 */
const ScaleToFit = ({ children, onMeasure }) => {
    const outerRef = useRef(null);
    const innerRef = useRef(null);
    const [scale, setScale] = useState(1);

    useEffect(() => {
        const outer = outerRef.current;
        const inner = innerRef.current;
        if (!outer || !inner || typeof ResizeObserver === 'undefined') return undefined;

        const update = () => {
            const frameHeight = outer.clientHeight;
            const naturalHeight = inner.scrollHeight;
            if (!frameHeight || !naturalHeight) return;
            onMeasure?.(naturalHeight);
            const next = naturalHeight > frameHeight
                ? Math.max(0.05, frameHeight / naturalHeight)
                : 1;
            setScale((prev) => (Math.abs(prev - next) > 0.01 ? next : prev));
        };

        const observer = new ResizeObserver(update);
        observer.observe(outer);
        observer.observe(inner);
        update();
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div ref={outerRef} className="slide-designer__fit">
            <div
                ref={innerRef}
                className="slide-designer__fit-inner"
                style={{ transform: `scale(${scale})`, transformOrigin: 'top center' }}
            >
                {children}
            </div>
        </div>
    );
};

// Frame geometry is stored on each block as percentages of the full 16:9
// slide: { x, y, w, h } with 0-100 on both axes. The PPTX exporter maps
// these percentages directly onto slide inches, so what you lay out here
// is exactly what exports.

const SNAP_TOLERANCE = 1.2;

const applySnaps = (frame) => {
    const snapped = { ...frame };
    const centerX = frame.x + frame.w / 2;
    const centerY = frame.y + frame.h / 2;
    if (Math.abs(centerX - 50) < SNAP_TOLERANCE) snapped.x = 50 - frame.w / 2;
    if (Math.abs(centerY - 50) < SNAP_TOLERANCE) snapped.y = 50 - frame.h / 2;
    if (Math.abs(frame.x - 4) < SNAP_TOLERANCE) snapped.x = 4;
    if (Math.abs(frame.x + frame.w - 96) < SNAP_TOLERANCE) snapped.x = 96 - frame.w;
    if (Math.abs(frame.y - 4) < SNAP_TOLERANCE) snapped.y = 4;
    if (Math.abs(frame.y + frame.h - 96) < SNAP_TOLERANCE) snapped.y = 96 - frame.h;
    return snapped;
};

// How close to the top of the stage an object can sit before its toolbar has to
// move inside it, as a percentage of slide height. The toolbar sits 34px above
// the object and a stage is roughly 520–560px tall, so ~7% is where it starts
// being clipped; 8 leaves a little room at narrower widths.
const TOOLBAR_FLIP_Y = 8;

const RESIZE_HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

// Block types that carry a citation that can be split off into its own movable
// object (see detachCitation).
const CITATION_SOURCE_TYPES = new Set(['image', 'gallery', 'video', 'youtube-playlist']);

// The block field that suppresses a source's own inline note once its citation
// has been detached, so the note is never shown twice.
const INCLUDE_CITATION_FIELD = {
    image: 'imageIncludeCitation',
    video: 'videoIncludeCitation',
    // A playlist card keeps its source under the video block's field names.
    'youtube-playlist': 'videoIncludeCitation'
};

// A short-lived unique id for objects created inside the designer (detached
// citation blocks, group ids). Kept local so the component has no dependency on
// the app-wide id generator.
let uidCounter = 0;
const uid = (prefix = 'obj') => `${prefix}-${Date.now().toString(36)}-${(uidCounter++).toString(36)}`;

// Does the block already carry enough info to render an APA note?
export const blockHasCitationContent = (block) => {
    if (!block || !CITATION_SOURCE_TYPES.has(block.type)) return false;
    if (block.type === 'gallery') return false; // galleries hold many items; detach per-image is out of scope
    // A playlist card offers the tear-off exactly when it is drawing a note of
    // its own — the same rule, so the action can never detach the bare "(n.d.)"
    // that a card carrying nothing but its default "YouTube" source would make.
    if (block.type === 'youtube-playlist') {
        if (block.videoIncludeCitation === false) return false;
        return hasPlaylistCitationDetails(block);
    }
    if (block.type === 'video') {
        if (block.videoIncludeCitation === false) return false;
        // A video's note shows the in-text citation *and* the watch URL, so a
        // video with nothing but a link still has a note worth tearing off.
        // The URL has to be a real address though: a raw-embed video keeps its
        // markup in `src`, and that is not something to hand to a reader.
        const watchUrl = (block.videoUrl || block.src || '').trim();
        return Boolean(
            block.videoAuthor || block.videoTitle || block.videoSource || block.videoDate
            || /^https?:\/\//i.test(watchUrl)
        );
    }
    if (block.imageIncludeCitation === false) return false;
    return Boolean(block.imageAuthor || block.imageTitle || block.imageSource || block.imageDate);
};

// Turn an image block's citation metadata into a standalone citation object of
// the shape apa-citation blocks (and the reference-list collector) understand.
export const imageBlockToCitation = (block) => ({
    id: uid('cite'),
    type: 'image',
    sourceType: block.imageSourceType || 'image',
    author: block.imageAuthor || '',
    title: block.imageTitle || '',
    source: block.imageSource || '',
    date: block.imageDate || '',
    // Embedded images live behind a provider URL; carrying it over keeps the
    // detached citation clickable in the reference list.
    url: block.imageUrl || block.imageEmbedUrl || '',
    page: block.imageIncludePage === false ? '' : (block.imagePage || ''),
    license: block.imageIncludeLicense === false ? '' : (block.imageLicense || ''),
    isAdapted: Boolean(block.imageIsAdapted),
    // Reference-entry fields: which reference template the source falls under,
    // its medium descriptor and, where the two differ, the sentence-case title.
    referenceCategory: block.imageReferenceCategory || '',
    medium: block.imageMedium || '',
    referenceTitle: block.imageReferenceTitle || '',
    articleTitle: block.imageArticleTitle || '',
    caption: block.caption || '',
    alt: block.alt || ''
});

// The same, for a video block. `sourceType: 'video'` is what makes the detached
// note format as an in-text citation rather than a figure note — matching the
// note the video itself was drawing — and what the reference-list collector
// reads to build the full reference entry.
export const videoBlockToCitation = (block) => ({
    id: uid('cite'),
    type: 'video',
    sourceType: 'video',
    author: block.videoAuthor || '',
    title: block.videoTitle || '',
    source: block.videoSource || '',
    date: block.videoDate || '',
    // buildVideoSourceMeta falls back to the embed src, so a video whose only
    // identity is its URL still tears off something clickable.
    url: block.videoUrl || block.src || ''
});

// A playlist card's citation. Same shape as a video's — `sourceType: 'video'`
// is what makes the detached note format as an in-text citation and what the
// reference-list collector reads — with the card's own display fields as a
// fallback and the playlist page as the address.
export const playlistBlockToCitation = (block) => ({
    id: uid('cite'),
    type: 'video',
    sourceType: 'video',
    author: block.videoAuthor || block.channel || '',
    title: block.videoTitle || block.title || '',
    source: block.videoSource || 'YouTube',
    date: block.videoDate || '',
    // The address travels with the citation so the reference list can build the
    // full entry — but the note itself does not print it, matching the note the
    // card was drawing before it was torn off.
    url: block.videoUrl || playlistPageUrl(block.playlistId) || '',
    showUrl: false
});

export const blockToDetachedCitation = (block) => {
    if (block?.type === 'youtube-playlist') return playlistBlockToCitation(block);
    return block?.type === 'video' ? videoBlockToCitation(block) : imageBlockToCitation(block);
};

// Height for a popped-out quote's frame, in slide percent.
//
// The APA Quote card is chunky — padding, a badge row, the quote at text-lg and
// a citation line — so an undersized frame crops it. Measured on the canvas: a
// three-line quote in a 46%-wide frame needs ~50% of the slide height, which is
// the calibration behind the numbers below.
const QUOTE_CARD_CHROME = 32;   // badge + padding + citation line
const QUOTE_CARD_LINE = 6;      // one wrapped line of quote text

// detectQuotes parses HTML, so memoize the count against the exact content the
// selected block is showing — re-selecting or nudging a block should not
// re-parse it.
let quoteCountCache = { content: null, count: 0 };
const countQuotes = (content) => {
    if (quoteCountCache.content === content) return quoteCountCache.count;
    const count = detectQuotes(content).length;
    quoteCountCache = { content, count };
    return count;
};

const estimateQuoteFrameHeight = (quote, widthPercent) => {
    const charsPerLine = Math.max(16, Math.round(widthPercent * 0.8));
    const lines = Math.max(1, Math.ceil((quote.quoteText || '').length / charsPerLine));
    return Math.min(65, QUOTE_CARD_CHROME + lines * QUOTE_CARD_LINE);
};

// Rectangle-overlap test in slide-percent space, used by the marquee selection.
const framesIntersect = (a, b) => !(
    a.x > b.x + b.w || a.x + a.w < b.x || a.y > b.y + b.h || a.y + a.h < b.y
);

// True when focus is inside editable content (a form field or rich-text area),
// so keyboard shortcuts like delete don't fire while the user is typing.
const isEditableTarget = (target) => {
    if (!target || typeof target.closest !== 'function') return false;
    if (target.isContentEditable) return true;
    return Boolean(target.closest('input, textarea, select, [contenteditable="true"], .ProseMirror'));
};

/**
 * Fixed 16:9 canvas where blocks are positioned and scaled freely,
 * PowerPoint-style. Move by dragging, resize with the corner/edge
 * handles, nudge with arrow keys, reorder depth with the toolbar.
 */
const SlideDesigner = ({
    blocks = [],
    isEditMode = false,
    onUpdateBlocks,
    onEditBlock,
    onDeleteBlock,
    onCanvasImageDrop,
    // Called with the image blocks a clipboard paste just placed, so the app
    // can offer the citation form for them. Placement has already happened —
    // this is the "…and now, optionally, cite them" half.
    onCanvasImagePaste,
    // Ruler/guide visibility is controlled by the parent (its toggle lives in
    // the mode banner). Falls back to local state if the parent doesn't wire it.
    showRulers: showRulersProp,
    onToggleRulers,
    renderBlock
}) => {
    const stageRef = useRef(null);
    // The fluid box the fixed-size stage is scaled into.
    const stageFrameRef = useRef(null);
    const stageScale = useStageScale(stageFrameRef);
    const dragRef = useRef(null);
    // Multiple objects can be selected at once. `selectedIds` is the source of
    // truth; `selectedId` is derived for the single-selection gizmo/toolbar.
    const [selectedIds, setSelectedIds] = useState([]);
    const selectedId = selectedIds.length === 1 ? selectedIds[0] : null;
    const [liveFrames, setLiveFrames] = useState({});
    // Block being typed into directly on the canvas (double-click), plus the
    // screen point that started it so the caret can land where the user clicked.
    const [inlineEdit, setInlineEdit] = useState(null); // { blockId, caretPoint }
    // Rubber-band selection rectangle (percent coords) while dragging on the
    // empty canvas, and whether an image file is being dragged over the stage.
    const [marquee, setMarquee] = useState(null);
    const marqueeRef = useRef(null);
    const [isFileOver, setIsFileOver] = useState(false);
    // Keep the latest blocks/selection reachable from the pointer handlers,
    // whose useCallbacks intentionally carry no deps.
    const blocksRef = useRef(blocks);
    useEffect(() => { blocksRef.current = blocks; }, [blocks]);
    const selectedIdsRef = useRef(selectedIds);
    useEffect(() => { selectedIdsRef.current = selectedIds; }, [selectedIds]);

    // Ruler + guide-line overlay. Guides live only here (never in block/slide
    // data) so they are purely an editing aid and never export or render.
    // The rulers on/off flag is controlled from the parent when provided, so
    // its toggle button can live in the mode banner instead of over the title.
    const [localShowRulers] = useState(loadRulerPref);
    const showRulers = showRulersProp !== undefined ? showRulersProp : localShowRulers;
    const [guides, setGuides] = useState(loadGuides);
    // Live preview while a guide is being dragged from a ruler or repositioned.
    const [guideDrag, setGuideDrag] = useState(null); // { axis, pos, index, origin }
    const guideDragRef = useRef(null);
    // Keep the latest guides reachable from the block-drag pointer handler,
    // whose useCallback intentionally has no deps.
    const guidesRef = useRef(guides);
    useEffect(() => { guidesRef.current = guides; }, [guides]);
    useEffect(() => { saveGuides(guides); }, [guides]);
    // Latest measured natural content height (px) per media block, and the
    // set of blocks already auto-fitted so it only happens on first import
    const measurementsRef = useRef({});
    const autoFittedRef = useRef(new Set());

    // Leaving edit mode (or losing the block to a delete/slide change) always
    // drops the in-place editor so it can't linger over presented content.
    useEffect(() => {
        if (!inlineEdit) return;
        if (!isEditMode || !blocks.some((b) => b.id === inlineEdit.blockId)) setInlineEdit(null);
    }, [isEditMode, blocks, inlineEdit]);

    const frameOf = useCallback((block, index) => {
        return liveFrames[block.id] || frameForBlock(block, index);
    }, [liveFrames]);

    const commitFrame = useCallback((blockId, frame) => {
        onUpdateBlocks(blocks.map((b) => (b.id === blockId ? { ...b, frame } : b)));
        setLiveFrames((prev) => {
            const next = { ...prev };
            delete next[blockId];
            return next;
        });
    }, [blocks, onUpdateBlocks]);

    // Commit several frames at once (multi-object move), in a single update so
    // the whole selection stays together in one undo step.
    const commitFrames = useCallback((frameMap) => {
        const ids = Object.keys(frameMap);
        if (!ids.length) return;
        onUpdateBlocks(blocksRef.current.map((b) => (frameMap[b.id] ? { ...b, frame: frameMap[b.id] } : b)));
        setLiveFrames((prev) => {
            const next = { ...prev };
            ids.forEach((id) => delete next[id]);
            return next;
        });
    }, [onUpdateBlocks]);

    // Every block id that shares a group with the given block (or just itself
    // when it belongs to no group). Selecting one group member selects them all.
    const groupMemberIds = useCallback((block) => {
        if (!block?.groupId) return [block.id];
        return blocksRef.current.filter((b) => b.groupId === block.groupId).map((b) => b.id);
    }, []);

    // Select a block (expanding to its whole group). Additive (shift/⌘) toggles
    // the group in/out; a plain click on an already-selected block keeps the
    // current multi-selection so it can be dragged as a unit.
    const selectBlock = useCallback((block, additive) => {
        const members = groupMemberIds(block);
        setSelectedIds((prev) => {
            if (additive) {
                const allIn = members.every((id) => prev.includes(id));
                return allIn
                    ? prev.filter((id) => !members.includes(id))
                    : Array.from(new Set([...prev, ...members]));
            }
            if (prev.includes(block.id)) return prev;
            return members;
        });
    }, [groupMemberIds]);

    // --- In-place text editing -------------------------------------------
    // Start typing straight into a text object on the canvas. Non-text blocks
    // (images, cards, code…) still route to the full editor modal.
    const beginInlineEdit = useCallback((block, caretPoint = null) => {
        if (!isEditMode) return false;
        if (!INLINE_TEXT_TYPES.has(block.type)) return false;
        setSelectedIds([block.id]);
        selectedIdsRef.current = [block.id];
        setInlineEdit({ blockId: block.id, caretPoint });
        return true;
    }, [isEditMode]);

    // Write the edited HTML back onto the block and leave inline edit mode.
    // Only writes when something actually changed, so a no-op edit doesn't
    // push a pointless undo step.
    const commitInlineEdit = useCallback((blockId, html, { refocusStage = true } = {}) => {
        setInlineEdit(null);
        // Escape/✓ leave the object selected, so hand focus to the stage and the
        // canvas shortcuts (Delete, arrows) keep working straight away.
        if (refocusStage) stageRef.current?.focus({ preventScroll: true });
        const target = blocksRef.current.find((b) => b.id === blockId);
        if (!target) return;
        const clean = sanitizeHtml(html ?? '');
        if ((target.content || '') === clean) return;
        onUpdateBlocks(blocksRef.current.map((b) => (b.id === blockId ? { ...b, content: clean } : b)));
    }, [onUpdateBlocks]);

    // "Open the full editor" from the inline ribbon: keep what was typed, then
    // hand the block to the parent's modal.
    const escalateToModal = useCallback((blockId, html) => {
        // The modal takes focus itself — don't fight it for the caret.
        commitInlineEdit(blockId, html, { refocusStage: false });
        onEditBlock?.(blockId);
    }, [commitInlineEdit, onEditBlock]);

    const onPointerMove = useCallback((event) => {
        const drag = dragRef.current;
        if (!drag) return;
        const { rect, start, mode, handle } = drag;
        const dx = ((event.clientX - start.clientX) / rect.width) * 100;
        const dy = ((event.clientY - start.clientY) / rect.height) * 100;
        let frame = { ...start.frame };

        // Resizing the box around a whole group: the box itself takes the drag,
        // and every member is carried along keeping its place inside it, so the
        // arrangement scales as one piece instead of the objects sliding about.
        if (mode === 'group-resize') {
            const nextBox = normalizeFrame(applyResizeHandle(start.frame, handle, dx, dy));
            // Corner drags scale the members' text too, matching what a corner
            // does to a single text box.
            const isCorner = handle.length === 2;
            const nextGroup = {};
            drag.members.forEach((member) => {
                nextGroup[member.blockId] = normalizeFrame(scaleFrameWithinBox(
                    member.frame,
                    start.frame,
                    nextBox,
                    { scaleText: isCorner && !SCALED_MEDIA_TYPES.has(member.blockType) }
                ));
            });
            drag.latest = nextBox;
            drag.latestMap = nextGroup;
            setLiveFrames((prev) => ({ ...prev, ...nextGroup }));
            return;
        }

        if (mode === 'move') {
            frame.x = start.frame.x + dx;
            frame.y = start.frame.y + dy;
        } else {
            frame = { ...frame, ...applyResizeHandle(start.frame, handle, dx, dy) };
            // Corner handles on text/title blocks scale the text with the box,
            // PowerPoint-style. Media already scales via ScaleToFit, and edge
            // handles keep resizing/reflowing the box without touching the text.
            const isCorner = handle.length === 2;
            if (isCorner && !SCALED_MEDIA_TYPES.has(drag.blockType)) {
                frame.s = textScaleForCornerResize(start.frame, frame, drag.textBounds || start.frame);
            }
        }
        frame = applySnaps(normalizeFrame(frame));
        // Snap the block onto any user-placed ruler guides (Illustrator-style).
        if (mode === 'move') frame = normalizeFrame(snapFrameToGuides(frame, guidesRef.current));
        drag.latest = frame;

        // Move: carry every other selected object by the same net delta so the
        // whole selection (or group) travels together.
        const nextLive = { [drag.blockId]: frame };
        if (mode === 'move' && drag.others?.length) {
            const gdx = frame.x - start.frame.x;
            const gdy = frame.y - start.frame.y;
            drag.others.forEach((other) => {
                const moved = normalizeFrame({ ...other.frame, x: other.frame.x + gdx, y: other.frame.y + gdy });
                nextLive[other.blockId] = moved;
            });
        }
        drag.latestMap = nextLive;
        setLiveFrames((prev) => ({ ...prev, ...nextLive }));
    }, []);

    const onPointerUp = useCallback(() => {
        const drag = dragRef.current;
        dragRef.current = null;
        window.removeEventListener('pointermove', onPointerMove);
        if (drag?.latestMap) commitFrames(drag.latestMap);
        else if (drag?.latest) commitFrame(drag.blockId, drag.latest);
        else if (drag) setLiveFrames((prev) => {
            const next = { ...prev };
            delete next[drag.blockId];
            return next;
        });
    }, [commitFrame, commitFrames, onPointerMove]);

    const startDrag = useCallback((event, block, index, mode, handle = null) => {
        if (!isEditMode || !stageRef.current) return;
        event.preventDefault();
        event.stopPropagation();
        // preventDefault above stops the browser handing focus to the stage, so
        // do it here: the canvas keyboard shortcuts (Delete, arrows, ⌘G) only
        // fire while the stage — or a selected object's tab — holds focus.
        if (!event.currentTarget?.dataset?.keepFocus) {
            stageRef.current.focus({ preventScroll: true });
        }

        // Resolve the selection this drag operates on *synchronously* (state
        // updates are async, so we can't read it back from selectedIdsRef in the
        // same tick). A plain click on an already-selected object keeps the
        // whole multi-selection so it can be dragged as a unit; otherwise the
        // grabbed object (expanded to its group) becomes the new selection.
        const additive = event.shiftKey || event.metaKey || event.ctrlKey;
        const members = groupMemberIds(block);
        const prev = selectedIdsRef.current;
        let nextSelection;
        if (additive) {
            const allIn = members.every((id) => prev.includes(id));
            nextSelection = allIn
                ? prev.filter((id) => !members.includes(id))
                : Array.from(new Set([...prev, ...members]));
        } else if (prev.includes(block.id)) {
            nextSelection = prev;
        } else {
            nextSelection = members;
        }
        setSelectedIds(nextSelection);
        selectedIdsRef.current = nextSelection;

        // Which other objects travel with this one on a move: everything in the
        // resolved selection except the grabbed block itself. Resize only ever
        // affects the single grabbed block.
        let others = [];
        if (mode === 'move') {
            const moving = new Set(nextSelection);
            moving.delete(block.id);
            others = blocksRef.current
                .map((b, i) => ({ block: b, index: i }))
                .filter(({ block: b }) => moving.has(b.id))
                .map(({ block: b, index: i }) => ({ blockId: b.id, frame: frameOf(b, i) }));
        }

        const stageRect = stageRef.current.getBoundingClientRect();
        dragRef.current = {
            blockId: block.id,
            blockType: block.type,
            mode,
            handle,
            others,
            rect: stageRect,
            textBounds: mode === 'resize' && !SCALED_MEDIA_TYPES.has(block.type)
                ? measureTextBounds(event.currentTarget?.closest?.('.slide-designer__item'), stageRect)
                : null,
            start: { clientX: event.clientX, clientY: event.clientY, frame: frameOf(block, index) },
            latest: null,
            latestMap: null
        };
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp, { once: true });
    }, [isEditMode, frameOf, groupMemberIds, onPointerMove, onPointerUp]);

    /**
     * Start resizing a whole group from its shared box.
     *
     * Unlike a single object's handles, which only ever touch the block they
     * belong to, this captures every selected member's frame up front and
     * rescales all of them against the box on each pointer move — so what the
     * author drags is the arrangement, not one object in it.
     */
    const startGroupResize = useCallback((event, handle, box) => {
        if (!isEditMode || !stageRef.current) return;
        event.preventDefault();
        event.stopPropagation();
        stageRef.current.focus({ preventScroll: true });

        const selected = new Set(selectedIdsRef.current);
        const members = blocksRef.current
            .map((block, index) => ({ block, index }))
            .filter(({ block }) => selected.has(block.id))
            .map(({ block, index }) => ({
                blockId: block.id,
                blockType: block.type,
                frame: frameOf(block, index)
            }));
        if (!members.length) return;

        dragRef.current = {
            mode: 'group-resize',
            handle,
            members,
            rect: stageRef.current.getBoundingClientRect(),
            start: { clientX: event.clientX, clientY: event.clientY, frame: box },
            latest: null,
            latestMap: null
        };
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp, { once: true });
    }, [isEditMode, frameOf, onPointerMove, onPointerUp]);

    // Combine every selected object (each expanded to its whole group) into a
    // new group, or split the selected groups apart again with ⌘⇧G.
    const groupSelected = useCallback(() => {
        const ids = new Set(selectedIdsRef.current);
        blocksRef.current.forEach((b) => {
            if (b.groupId && ids.has(b.id)) {
                blocksRef.current.forEach((sib) => { if (sib.groupId === b.groupId) ids.add(sib.id); });
            }
        });
        if (ids.size < 2) return;
        const groupId = uid('grp');
        onUpdateBlocks(blocksRef.current.map((b) => (ids.has(b.id) ? { ...b, groupId } : b)));
        setSelectedIds(Array.from(ids));
    }, [onUpdateBlocks]);

    const ungroupSelected = useCallback(() => {
        const ids = new Set(selectedIdsRef.current);
        let changed = false;
        const next = blocksRef.current.map((b) => {
            if (ids.has(b.id) && b.groupId) {
                changed = true;
                const { groupId, ...rest } = b;
                return rest;
            }
            return b;
        });
        if (changed) onUpdateBlocks(next);
    }, [onUpdateBlocks]);

    // --- Copy / cut / paste -----------------------------------------------
    // The clipboard lives outside the component (see utils/blockClipboard), so
    // an object cut on one slide is still there after navigating to another.
    const copySelection = useCallback(({ cut = false } = {}) => {
        const ids = new Set(selectedIdsRef.current);
        if (!ids.size) return 0;
        // Grab whole groups, the way dragging and deleting already do.
        blocksRef.current.forEach((block) => {
            if (block.groupId && ids.has(block.id)) {
                blocksRef.current.forEach((sib) => { if (sib.groupId === block.groupId) ids.add(sib.id); });
            }
        });
        const copied = blocksRef.current.filter((block) => ids.has(block.id));
        const count = writeBlockClipboard(copied);
        if (cut && count) {
            onUpdateBlocks(blocksRef.current.filter((block) => !ids.has(block.id)));
            setSelectedIds([]);
            // Focus follows the removed object's tab handle, so give it back.
            stageRef.current?.focus({ preventScroll: true });
        }
        return count;
    }, [onUpdateBlocks]);

    // Repeat pastes cascade rather than restacking on the same spot.
    const pasteRunRef = useRef({ signature: null, count: 0 });

    const pasteClipboard = useCallback(() => {
        const clipboard = readBlockClipboard();
        if (!clipboard.length) return 0;

        // Pasting the same clipboard again on the same slide steps further out;
        // a new copy, or a different slide, starts the cascade over.
        const signature = clipboard.map((block) => block.id).join('|');
        const currentIds = new Set(blocksRef.current.map((block) => block.id));
        const alreadyHere = clipboard.some((block) => currentIds.has(block.id));
        const run = pasteRunRef.current;
        run.count = run.signature === signature ? run.count + 1 : 1;
        run.signature = signature;
        // Pasting onto a slide the objects are not on can land at their own
        // coordinates; only a same-slide paste has to dodge the original.
        const offset = alreadyHere || run.count > 1 ? 3 * run.count : 0;

        const pasted = prepareBlocksForPaste(clipboard, () => uid('block'), { offset, normalizeFrame });
        onUpdateBlocks([...blocksRef.current, ...pasted]);
        setSelectedIds(pasted.map((block) => block.id));
        return pasted.length;
    }, [onUpdateBlocks]);

    /**
     * Paste pictures from the system clipboard straight onto the slide.
     *
     * Copy a diagram out of a deck, a figure off a web page or a screenshot,
     * press ⌘/Ctrl+V, and it lands here — the shortest path from seeing a
     * picture to having it on a slide, and the one a PowerPoint author reaches
     * for first.
     *
     * The pictures are placed immediately, then handed to the app so it can ask
     * about citations. Placing first is deliberate: a citation is optional, and
     * making the picture wait behind a dialog would put a form between the
     * author and the thing they were actually doing.
     */
    const pasteImageFiles = useCallback(async (files) => {
        const named = files.map((file, index) => nameClipboardImage(file, index + 1));

        const created = [];
        for (let index = 0; index < named.length; index += 1) {
            const file = named[index];
            try {
                // Sequential rather than parallel: the reads are fast, and a
                // deterministic order keeps the cascade matching paste order.
                // eslint-disable-next-line no-await-in-loop
                const src = await fileToDataUrl(file);
                if (!src) continue;
                // eslint-disable-next-line no-await-in-loop
                const natural = await measureImage(src);
                created.push({
                    id: uid('block'),
                    type: 'image',
                    src,
                    alt: file.name,
                    size: 'large',
                    caption: '',
                    imageShowCaption: true,
                    // Off until there is something to cite. An image block that
                    // claims a citation but carries no fields renders the stub
                    // note "Note. From n.d." under the picture — which is what
                    // every pasted picture would wear until it was cited, and
                    // most never need citing at all.
                    imageIncludeCitation: false,
                    frame: normalizeFrame(frameForPastedImage({
                        aspect: natural.width / natural.height,
                        index,
                        stageWidth: STAGE_WIDTH,
                        stageHeight: STAGE_HEIGHT
                    }))
                });
            } catch {
                /* unreadable picture — skip it and place the rest */
            }
        }

        if (!created.length) return 0;
        onUpdateBlocks([...blocksRef.current, ...created]);
        setSelectedIds(created.map((block) => block.id));
        onCanvasImagePaste?.(created);
        return created.length;
    }, [onUpdateBlocks, onCanvasImagePaste]);

    const handleStageKeyDown = useCallback((event) => {
        if (!isEditMode) return;
        // Don't act on keyboard shortcuts while the user is editing card content.
        if (isEditableTarget(event.target)) return;

        // ⌘/Ctrl+G groups the selection; add Shift to ungroup.
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'g') {
            event.preventDefault();
            if (event.shiftKey) ungroupSelected();
            else groupSelected();
            return;
        }

        // ⌘/Ctrl+A selects every object on the slide.
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'a') {
            event.preventDefault();
            setSelectedIds(blocks.map((b) => b.id));
            return;
        }

        if (event.key === 'Escape') { setSelectedIds([]); return; }

        if (!selectedIds.length) return;

        // Enter/F2 on a single selected text object starts typing in place,
        // matching the double-click behaviour (PowerPoint's F2).
        if (event.key === 'Enter' || event.key === 'F2') {
            const target = blocks.find((b) => b.id === selectedIds[0]);
            if (selectedIds.length === 1 && target && beginInlineEdit(target)) {
                event.preventDefault();
                return;
            }
        }

        // Delete every selected object. A single selection routes through the
        // parent's confirm dialog (toolbar parity); multiple delete at once.
        if (event.key === 'Delete' || event.key === 'Backspace') {
            event.preventDefault();
            const ids = selectedIds;
            setSelectedIds([]);
            if (ids.length === 1) {
                onDeleteBlock?.(ids[0]);
            } else {
                onUpdateBlocks(blocks.filter((b) => !ids.includes(b.id)));
            }
            // The focused tab handle disappears with its object, so hand focus
            // back to the stage and keep the keyboard working.
            stageRef.current?.focus({ preventScroll: true });
            return;
        }

        const deltas = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
        const delta = deltas[event.key];
        if (!delta) return;
        event.preventDefault();
        const step = event.shiftKey ? 4 : 1;
        const frameMap = {};
        blocks.forEach((b, index) => {
            if (!selectedIds.includes(b.id)) return;
            const current = frameOf(b, index);
            frameMap[b.id] = normalizeFrame({
                ...current,
                x: current.x + delta[0] * step,
                y: current.y + delta[1] * step
            });
        });
        commitFrames(frameMap);
    }, [isEditMode, selectedIds, blocks, frameOf, commitFrames, onDeleteBlock, onUpdateBlocks,
        groupSelected, ungroupSelected, beginInlineEdit]);

    // Copy/cut listen on the document rather than the canvas.
    //
    // The canvas-scoped shortcuts (delete, nudge, group) only make sense with
    // the canvas focused, but the clipboard ones do not: the natural move is to
    // cut, click the arrow to the next slide — focus now on that button — and
    // press ⌘/Ctrl+V. Requiring a click on the canvas first would feel broken.
    // The handler still stands down whenever the caret is in a field or an
    // editor, or while a content modal is open, so it never eats a real copy.
    useEffect(() => {
        if (!isEditMode) return undefined;
        const onKeyDown = (event) => {
            if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
            const key = event.key.toLowerCase();
            if (key !== 'c' && key !== 'x') return;
            if (isEditableTarget(event.target)) return;
            if (document.querySelector('[data-app-modal]')) return;
            // A text selection means the user is copying words, not objects.
            if (!window.getSelection()?.isCollapsed) return;
            if (!selectedIdsRef.current.length) return;
            event.preventDefault();
            copySelection({ cut: key === 'x' });
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isEditMode, copySelection]);

    /**
     * Paste arbitrates between two clipboards.
     *
     * The object clipboard is ours and survives a reload (blockClipboard.js);
     * the system clipboard is the one that holds a picture copied out of
     * PowerPoint. Deciding between them on ⌘/Ctrl+V *keydown* is not possible —
     * the system clipboard cannot be read synchronously there — and pasting
     * objects on keydown would cancel the paste event, so a stored object copy
     * from days ago would beat the picture copied a second ago and images could
     * effectively never be pasted at all.
     *
     * Listening for the paste event instead puts the actual clipboard contents
     * in hand before choosing. A picture wins, because a picture on the system
     * clipboard is by definition what was copied most recently. Anything else
     * falls through to the object clipboard, so ⌘/Ctrl+V on objects is
     * unchanged. It also picks up Paste from the browser's own Edit menu, which
     * the keydown handler never saw.
     */
    useEffect(() => {
        if (!isEditMode) return undefined;
        const onPaste = (event) => {
            if (isEditableTarget(event.target)) return;
            if (document.querySelector('[data-app-modal]')) return;

            const images = imageFilesFromClipboard(event.clipboardData);
            if (images.length) {
                event.preventDefault();
                pasteImageFiles(images);
                return;
            }
            if (pasteClipboard()) event.preventDefault();
        };
        document.addEventListener('paste', onPaste);
        return () => document.removeEventListener('paste', onPaste);
    }, [isEditMode, pasteClipboard, pasteImageFiles]);

    // Step a text block's font size up or down. This drives the block's
    // `frame.s` text-scale multiplier — the same value the corner handles set —
    // so it scales the block's text on the canvas and carries into the export.
    const adjustTextScale = useCallback((block, index, delta) => {
        const current = frameOf(block, index);
        const nextScale = Math.min(
            MAX_TEXT_SCALE,
            Math.max(MIN_TEXT_SCALE, Math.round(((current.s || 1) + delta) * 100) / 100)
        );
        commitFrame(block.id, normalizeFrame({ ...current, s: nextScale }));
    }, [frameOf, commitFrame]);

    const resetTextScale = useCallback((block, index) => {
        const current = frameOf(block, index);
        commitFrame(block.id, normalizeFrame({ ...current, s: 1 }));
    }, [frameOf, commitFrame]);

    // Size a media block's frame height to hug its content at the current
    // frame width, so the selection gizmo matches the visible media
    const fitFrameToContent = useCallback((block, index) => {
        const naturalHeight = measurementsRef.current[block.id];
        const stage = stageRef.current;
        if (!naturalHeight || !stage?.clientHeight) return;
        const current = frameForBlock(block, index);
        const fittedH = Math.min(96, Math.max(4, (naturalHeight / stage.clientHeight) * 100 + 1));
        if (Math.abs(fittedH - current.h) < 1.5) return;
        commitFrame(block.id, normalizeFrame({ ...current, h: fittedH }));
    }, [commitFrame]);

    const handleMeasure = useCallback((block, index, naturalHeight) => {
        measurementsRef.current[block.id] = naturalHeight;
        // Auto-fit only blocks that have never been positioned (fresh imports)
        if (!block.frame && !autoFittedRef.current.has(block.id)) {
            autoFittedRef.current.add(block.id);
            fitFrameToContent(block, index);
        }
    }, [fitFrameToContent]);

    const changeDepth = useCallback((blockId, direction) => {
        const index = blocks.findIndex((b) => b.id === blockId);
        const target = index + direction;
        if (index === -1 || target < 0 || target >= blocks.length) return;
        const next = [...blocks];
        [next[index], next[target]] = [next[target], next[index]];
        onUpdateBlocks(next);
    }, [blocks, onUpdateBlocks]);

    // Split a media block's citation out into its own movable object: a
    // standalone apa-citation block positioned in a full-width row beneath the
    // image or video. Because it is a real apa-citation block it is picked up by
    // the reference-list collector and can be saved to the citation library, and
    // it renders as a bare figure note (no card background). The source block
    // stops rendering/collecting its own note so nothing is duplicated.
    const detachCitation = useCallback((blockId) => {
        const index = blocks.findIndex((b) => b.id === blockId);
        if (index === -1) return;
        const source = blocks[index];
        if (!blockHasCitationContent(source)) return;
        const sourceFrame = frameOf(source, index);
        const citationFrame = normalizeFrame({
            x: sourceFrame.x,
            y: sourceFrame.y + sourceFrame.h + 1,
            w: Math.max(sourceFrame.w, 40),
            h: 7
        });
        const citationBlock = {
            id: uid('block'),
            type: 'apa-citation',
            variant: 'figure-note',
            sourceBlockId: source.id,
            citations: [blockToDetachedCitation(source)],
            frame: citationFrame
        };
        const includeField = INCLUDE_CITATION_FIELD[source.type] || 'imageIncludeCitation';
        const next = blocks.map((b) => (b.id === blockId ? { ...b, [includeField]: false } : b));
        next.splice(index + 1, 0, citationBlock);
        onUpdateBlocks(next);
        setSelectedIds([citationBlock.id]);
    }, [blocks, frameOf, onUpdateBlocks]);

    // Lift the quotes out of a text block into their own APA Quote blocks —
    // the same idea as detaching an image's citation, applied to prose. Each
    // quote becomes a real apa-quote block (so the reference-list collector
    // picks it up), stacked under the text block, and the paragraph it came
    // from is removed so the quote is not shown twice.
    const extractQuotes = useCallback((blockId) => {
        const index = blocks.findIndex((b) => b.id === blockId);
        if (index === -1) return;
        const source = blocks[index];
        const quotes = detectQuotes(source.content);
        if (!quotes.length) return;

        const sourceFrame = frameOf(source, index);
        const width = Math.max(sourceFrame.w, 40);
        // Stack the quotes under the text block while the slide has room. An
        // APA Quote card is tall, so a second one often will not fit — rather
        // than pile it exactly on top of the first, cascade it a little the way
        // a design tool offsets a pasted object, leaving it obvious and easy to
        // drag somewhere sensible.
        let cursorY = sourceFrame.y + sourceFrame.h + 1.5;
        let cascade = 0;
        const quoteBlocks = quotes.map((quote) => {
            const height = estimateQuoteFrameHeight(quote, width);
            let x = sourceFrame.x;
            let y = cursorY;
            if (y + height > 97) {
                cascade += 1;
                x = Math.min(96 - width, sourceFrame.x + cascade * 4);
                y = Math.min(97 - height, 3 + cascade * 4);
            } else {
                cursorY = y + height + 1.5;
            }
            return {
                id: uid('block'),
                ...quoteToBlockData(quote),
                sourceBlockId: source.id,
                frame: normalizeFrame({ x: Math.max(2, x), y: Math.max(2, y), w: width, h: height })
            };
        });

        const remaining = removeQuotesAt(source.content, quotes.map((quote) => quote.elementIndex));
        const next = blocks.map((b) => (b.id === blockId ? { ...b, content: remaining } : b));
        next.splice(index + 1, 0, ...quoteBlocks);
        onUpdateBlocks(next);
        setSelectedIds(quoteBlocks.map((block) => block.id));
    }, [blocks, frameOf, onUpdateBlocks]);

    // --- Marquee (rubber-band) selection ----------------------------------
    // Dragging across the empty canvas sweeps a selection box; every object it
    // touches (expanded to whole groups) becomes selected on release.
    const onMarqueePointerMove = useCallback((event) => {
        const drag = marqueeRef.current;
        if (!drag) return;
        const x = ((event.clientX - drag.rect.left) / drag.rect.width) * 100;
        const y = ((event.clientY - drag.rect.top) / drag.rect.height) * 100;
        drag.cur = { x: Math.min(100, Math.max(0, x)), y: Math.min(100, Math.max(0, y)) };
        setMarquee({ ...drag });
    }, []);

    const onMarqueePointerUp = useCallback(() => {
        const drag = marqueeRef.current;
        marqueeRef.current = null;
        window.removeEventListener('pointermove', onMarqueePointerMove);
        setMarquee(null);
        if (!drag) return;
        const box = {
            x: Math.min(drag.origin.x, drag.cur.x),
            y: Math.min(drag.origin.y, drag.cur.y),
            w: Math.abs(drag.cur.x - drag.origin.x),
            h: Math.abs(drag.cur.y - drag.origin.y)
        };
        // A tiny box is really just a click on empty canvas → clear selection.
        if (box.w < 1 && box.h < 1) { setSelectedIds([]); return; }
        const hitGroups = new Set();
        const hits = [];
        blocksRef.current.forEach((b, i) => {
            if (framesIntersect(frameOf(b, i), box)) {
                if (b.groupId) hitGroups.add(b.groupId);
                else hits.push(b.id);
            }
        });
        blocksRef.current.forEach((b) => {
            if (b.groupId && hitGroups.has(b.groupId)) hits.push(b.id);
        });
        setSelectedIds((prev) => (drag.additive ? Array.from(new Set([...prev, ...hits])) : hits));
    }, [onMarqueePointerMove, frameOf]);

    const onStagePointerDown = useCallback((event) => {
        if (!isEditMode || !stageRef.current) return;
        const rect = stageRef.current.getBoundingClientRect();
        const origin = {
            x: ((event.clientX - rect.left) / rect.width) * 100,
            y: ((event.clientY - rect.top) / rect.height) * 100
        };
        marqueeRef.current = { rect, origin, cur: origin, additive: event.shiftKey || event.metaKey || event.ctrlKey };
        setMarquee(marqueeRef.current);
        window.addEventListener('pointermove', onMarqueePointerMove);
        window.addEventListener('pointerup', onMarqueePointerUp, { once: true });
    }, [isEditMode, onMarqueePointerMove, onMarqueePointerUp]);

    // --- Drag an image file straight onto the canvas ----------------------
    // Hands the dropped image(s) to the app, which opens the image importer
    // modal pre-loaded with the file, ready for setup.
    const hasImageFiles = (event) => Array.from(event.dataTransfer?.items || [])
        .some((item) => item.kind === 'file' && item.type.startsWith('image/'));

    const onStageDragOver = useCallback((event) => {
        if (!isEditMode || !onCanvasImageDrop) return;
        if (!hasImageFiles(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        setIsFileOver(true);
    }, [isEditMode, onCanvasImageDrop]);

    const onStageDragLeave = useCallback((event) => {
        // Only clear when the pointer actually leaves the stage, not when it
        // crosses onto a child element.
        if (event.currentTarget.contains(event.relatedTarget)) return;
        setIsFileOver(false);
    }, []);

    const onStageDrop = useCallback((event) => {
        if (!isEditMode || !onCanvasImageDrop) return;
        setIsFileOver(false);
        const files = Array.from(event.dataTransfer?.files || []).filter((f) => f.type.startsWith('image/'));
        if (!files.length) return;
        event.preventDefault();
        onCanvasImageDrop(files);
    }, [isEditMode, onCanvasImageDrop]);

    // --- Ruler guides -----------------------------------------------------
    // A guide's axis is its orientation: 'v' = vertical line (positioned by an
    // x%), 'h' = horizontal line (positioned by a y%). The top ruler drops
    // horizontal guides; the left ruler drops vertical ones — like Illustrator.
    const clearGuides = useCallback(() => setGuides(emptyGuides()), []);

    const guidePosFromEvent = useCallback((axis, rect, event) => {
        if (axis === 'v') return ((event.clientX - rect.left) / rect.width) * 100;
        return ((event.clientY - rect.top) / rect.height) * 100;
    }, []);

    const onGuidePointerMove = useCallback((event) => {
        const drag = guideDragRef.current;
        if (!drag) return;
        const pos = guidePosFromEvent(drag.axis, drag.rect, event);
        // Allow the pointer to travel slightly off-stage so an existing guide
        // can be dragged away to delete it.
        const clamped = Math.min(110, Math.max(-10, pos));
        // Keep the ref in sync so the pointer-up commit reads the final
        // position (not the stale one captured when the drag began).
        drag.pos = clamped;
        setGuideDrag({ ...drag });
    }, [guidePosFromEvent]);

    const onGuidePointerUp = useCallback(() => {
        const drag = guideDragRef.current;
        guideDragRef.current = null;
        window.removeEventListener('pointermove', onGuidePointerMove);
        if (!drag) { setGuideDrag(null); return; }

        const { axis, index, origin, pos } = drag;
        const inStage = pos >= 0 && pos <= 100;
        setGuides((prev) => {
            const list = [...(prev[axis] || [])];
            if (origin === 'existing') {
                if (inStage) list[index] = tidyGuidePosition(pos);
                else list.splice(index, 1); // dragged off-stage → delete
            } else if (inStage) {
                list.push(tidyGuidePosition(pos)); // new guide from the ruler
            }
            return sanitizeGuides({ ...prev, [axis]: list });
        });
        setGuideDrag(null);
    }, [onGuidePointerMove]);

    const beginGuide = useCallback((event, axis, index = null) => {
        if (!isEditMode || !stageRef.current) return;
        event.preventDefault();
        event.stopPropagation();
        const rect = stageRef.current.getBoundingClientRect();
        const origin = index === null ? 'ruler' : 'existing';
        const pos = origin === 'existing'
            ? guides[axis][index]
            : guidePosFromEvent(axis, rect, event);
        guideDragRef.current = { axis, rect, index, origin, pos };
        setGuideDrag({ axis, pos, index, origin });
        window.addEventListener('pointermove', onGuidePointerMove);
        window.addEventListener('pointerup', onGuidePointerUp, { once: true });
    }, [isEditMode, guides, guidePosFromEvent, onGuidePointerMove, onGuidePointerUp]);

    const removeGuide = useCallback((axis, index) => {
        setGuides((prev) => {
            const list = [...(prev[axis] || [])];
            list.splice(index, 1);
            return { ...prev, [axis]: list };
        });
    }, []);

    const rulersActive = isEditMode && showRulers;
    const guideCount = guides.v.length + guides.h.length;
    // The guide being repositioned is hidden while its live preview is shown.
    const draggingExisting = guideDrag && guideDrag.origin === 'existing' ? guideDrag : null;

    // Bounding box (percent coords) for every group, so grouped objects show a
    // dashed frame around the whole set — the visual cue that they're grouped.
    const groupBoxes = useMemo(() => {
        const acc = {};
        blocks.forEach((b, i) => {
            if (!b.groupId) return;
            const f = frameOf(b, i);
            const g = acc[b.groupId] || { x0: 100, y0: 100, x1: 0, y1: 0 };
            g.x0 = Math.min(g.x0, f.x);
            g.y0 = Math.min(g.y0, f.y);
            g.x1 = Math.max(g.x1, f.x + f.w);
            g.y1 = Math.max(g.y1, f.y + f.h);
            acc[b.groupId] = g;
        });
        return acc;
    }, [blocks, frameOf]);

    const selectedGroupIds = new Set(
        blocks.filter((b) => b.groupId && selectedIds.includes(b.id)).map((b) => b.groupId)
    );

    /**
     * The one group the selection *is*, if it is exactly one group.
     *
     * The shared scale box only makes sense when the selection and a group are
     * the same set of objects. Two groups at once, or a group plus a loose
     * object, would need a box that resizes things which are not grouped —
     * so those keep the existing per-object behaviour instead.
     */
    const groupSelection = useMemo(() => {
        if (!isEditMode || selectedGroupIds.size !== 1) return null;
        const [groupId] = Array.from(selectedGroupIds);
        const members = blocks
            .map((block, index) => ({ block, index }))
            .filter(({ block }) => block.groupId === groupId);
        // Every member selected, and nothing outside the group selected too.
        if (members.length !== selectedIds.length) return null;
        if (!members.every(({ block }) => selectedIds.includes(block.id))) return null;

        const box = boundingBoxOf(members.map(({ block, index }) => frameOf(block, index)));
        if (!box) return null;
        return { groupId, box, count: members.length, lead: members[0] };
        // selectedGroupIds is rebuilt each render from selectedIds, so keying
        // the memo on the ids themselves keeps it from thrashing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode, blocks, selectedIds, frameOf]);
    const canGroup = selectedIds.length >= 2;
    const canUngroup = selectedGroupIds.size > 0;

    return (
        <div className={`slide-designer-frame ${rulersActive ? 'slide-designer-frame--rulers' : ''}`}>
            {isEditMode && (
                <div className="slide-designer__rulerbar" onPointerDown={(e) => e.stopPropagation()}>
                    {(canGroup || canUngroup) && (
                        <>
                            {canGroup && (
                                <button
                                    type="button"
                                    className="slide-designer__rulerbtn"
                                    onClick={groupSelected}
                                    title="Group the selected objects so they move and resize together (Ctrl/⌘+G)."
                                >
                                    ⧉ Group ({selectedIds.length})
                                </button>
                            )}
                            {canUngroup && (
                                <button
                                    type="button"
                                    className="slide-designer__rulerbtn"
                                    onClick={ungroupSelected}
                                    title="Break the selected group apart (Ctrl/⌘+Shift+G)."
                                >
                                    ⤬ Ungroup
                                </button>
                            )}
                        </>
                    )}
                    {/* The rulers/guides on-off toggle now lives in the mode
                        banner (kept off the title bar). This bar keeps the
                        contextual guide + grouping controls. */}
                    {rulersActive && (
                        <button
                            type="button"
                            className="slide-designer__rulerbtn"
                            onClick={clearGuides}
                            disabled={guideCount === 0}
                            title="Remove all guide lines. Guides otherwise stay exactly where you place them across slides and reloads."
                        >
                            Reset guides{guideCount > 0 ? ` (${guideCount})` : ''}
                        </button>
                    )}
                </div>
            )}
            {rulersActive && (
                <>
                    <div className="slide-ruler-corner" aria-hidden="true" />
                    <div
                        className="slide-ruler slide-ruler--top"
                        onPointerDown={(e) => beginGuide(e, 'h')}
                        title="Drag down to add a horizontal guide"
                    >
                        {MINOR_TICKS.map((p) => (
                            <span key={`tm${p}`} className="slide-ruler__tick slide-ruler__tick--minor" style={{ left: `${p}%` }} />
                        ))}
                        {MAJOR_TICKS.map((p) => (
                            <span key={`tM${p}`} className="slide-ruler__tick slide-ruler__tick--major" style={{ left: `${p}%` }}>
                                <span className="slide-ruler__label">{p}</span>
                            </span>
                        ))}
                    </div>
                    <div
                        className="slide-ruler slide-ruler--left"
                        onPointerDown={(e) => beginGuide(e, 'v')}
                        title="Drag right to add a vertical guide"
                    >
                        {MINOR_TICKS.map((p) => (
                            <span key={`lm${p}`} className="slide-ruler__tick slide-ruler__tick--minor" style={{ top: `${p}%` }} />
                        ))}
                        {MAJOR_TICKS.map((p) => (
                            <span key={`lM${p}`} className="slide-ruler__tick slide-ruler__tick--major" style={{ top: `${p}%` }}>
                                <span className="slide-ruler__label">{p}</span>
                            </span>
                        ))}
                    </div>
                </>
            )}
        {/* Takes the layout space and reports the width to scale to; the stage
            inside is a constant size so everything on it scales together. */}
        <div className="slide-designer-fit" ref={stageFrameRef}>
        <div
            ref={stageRef}
            className={`slide-designer ${isEditMode ? 'slide-designer--edit' : ''} ${isFileOver ? 'slide-designer--fileover' : ''}`}
            style={{
                width: `${STAGE_WIDTH}px`,
                height: `${STAGE_HEIGHT}px`,
                // Hidden until measured, so a full-size canvas never flashes
                // across the page on first paint.
                transform: `scale(${stageScale == null ? 0 : stageScale})`
            }}
            tabIndex={isEditMode ? 0 : -1}
            onKeyDown={handleStageKeyDown}
            onPointerDown={onStagePointerDown}
            onDragOver={onStageDragOver}
            onDragLeave={onStageDragLeave}
            onDrop={onStageDrop}
        >
            {/* Dashed frame around each group so grouped objects read as a set. */}
            {isEditMode && Object.entries(groupBoxes).map(([groupId, box]) => (
                <div
                    key={`group-${groupId}`}
                    className={`slide-designer__groupbox ${selectedGroupIds.has(groupId) ? 'is-selected' : ''}`}
                    style={{
                        left: `${box.x0}%`,
                        top: `${box.y0}%`,
                        width: `${box.x1 - box.x0}%`,
                        height: `${box.y1 - box.y0}%`
                    }}
                    aria-hidden="true"
                >
                    {/* The selected group gets a real, grabbable tab below
                        instead of this label, so only unselected groups need
                        naming here. */}
                    {!selectedGroupIds.has(groupId) && (
                        <span className="slide-designer__grouptag">⧉ Group</span>
                    )}
                </div>
            ))}

            {/* One scale box for a whole selected group: a tab to move it by
                and handles to resize every member at once. It replaces the
                per-object tabs, which on a six-object group were six labels
                competing for the same corner. */}
            {groupSelection && (
                <div
                    className="slide-designer__groupscale"
                    style={{
                        left: `${groupSelection.box.x}%`,
                        top: `${groupSelection.box.y}%`,
                        width: `${groupSelection.box.w}%`,
                        height: `${groupSelection.box.h}%`
                    }}
                >
                    <div
                        className={`slide-designer__tab slide-designer__tab--group ${groupSelection.box.y < 4 ? 'slide-designer__tab--inside' : ''}`}
                        role="button"
                        tabIndex={0}
                        data-keep-focus="1"
                        aria-label={`Group of ${groupSelection.count} objects selected. Press Delete or Backspace to remove it.`}
                        title={`Group of ${groupSelection.count} · drag to move all of them · drag a handle to scale them together · Ctrl/⌘+Shift+G to ungroup`}
                        onPointerDown={(e) => {
                            // Moving the group is the ordinary multi-select
                            // move, started from any member — the selection
                            // already holds all of them.
                            e.currentTarget.focus({ preventScroll: true });
                            startDrag(e, groupSelection.lead.block, groupSelection.lead.index, 'move');
                        }}
                    >
                        <span className="slide-designer__tab-grip" aria-hidden="true">⠿</span>
                        {`Group (${groupSelection.count})`}
                    </div>
                    {RESIZE_HANDLES.map((handle) => (
                        <div
                            key={`group-handle-${handle}`}
                            className={`slide-designer__handle slide-designer__handle--${handle}`}
                            title={handle.length === 2
                                ? 'Drag to scale the whole group; text scales with it'
                                : 'Drag to stretch the whole group'}
                            onPointerDown={(e) => startGroupResize(e, handle, groupSelection.box)}
                        />
                    ))}
                </div>
            )}
            {blocks.map((block, index) => {
                const frame = frameOf(block, index);
                const isSelected = isEditMode && selectedIds.includes(block.id);
                const isSoleSelection = isEditMode && selectedId === block.id;
                const canDetach = blockHasCitationContent(block);
                // Only offered when the block actually holds a cited quote, so
                // the toolbar stays quiet on ordinary prose. Counted only for
                // the selected block — parsing every block's HTML on every
                // render would be wasted work on a busy slide.
                const quoteCount = isSoleSelection && INLINE_TEXT_TYPES.has(block.type)
                    ? countQuotes(block.content)
                    : 0;
                const canEditInline = isEditMode && INLINE_TEXT_TYPES.has(block.type);
                const isInlineEditing = inlineEdit?.blockId === block.id;
                return (
                    <div
                        key={block.id}
                        data-block-id={block.id}
                        className={`slide-designer__item ${isSelected ? 'slide-designer__item--selected' : ''} ${block.groupId ? 'slide-designer__item--grouped' : ''} ${isInlineEditing ? 'slide-designer__item--editing' : ''}`}
                        style={{
                            left: `${frame.x}%`,
                            top: `${frame.y}%`,
                            width: `${frame.w}%`,
                            height: `${frame.h}%`,
                            // Blocks stack in document order; the one being typed
                            // into jumps to the front so neither its editing
                            // surface nor its ribbon can be covered by a
                            // later block.
                            zIndex: isInlineEditing ? 500 : 10 + index,
                            '--text-scale': frame.s || 1
                        }}
                        onPointerDown={(e) => {
                            if (isEditMode) {
                                e.stopPropagation();
                                selectBlock(block, e.shiftKey || e.metaKey || e.ctrlKey);
                            }
                        }}
                    >
                        <div className="slide-designer__content">
                            {isInlineEditing ? null : SCALED_MEDIA_TYPES.has(block.type)
                                ? (
                                    <ScaleToFit onMeasure={(naturalHeight) => handleMeasure(block, index, naturalHeight)}>
                                        {renderBlock(block)}
                                    </ScaleToFit>
                                )
                                : renderBlock(block)}
                        </div>
                        {isInlineEditing && (
                            <InlineTextEditor
                                block={block}
                                // Two rows of controls stand about 11% of the
                                // stage tall, so a box any higher than that
                                // would have its ribbon clipped by the stage's
                                // top edge and must take it underneath instead.
                                ribbonBelow={frame.y < 11}
                                ribbonRight={frame.x + frame.w > 62}
                                caretPoint={inlineEdit.caretPoint}
                                onCommit={(html, options) => commitInlineEdit(block.id, html, options)}
                                onOpenModal={(html) => escalateToModal(block.id, html)}
                            />
                        )}
                        {isEditMode && !isInlineEditing && (
                            <div
                                className="slide-designer__capture"
                                onPointerDown={(e) => startDrag(e, block, index, 'move')}
                                onDoubleClick={(e) => {
                                    // Text objects are edited straight on the slide;
                                    // everything else opens the full editor modal.
                                    if (beginInlineEdit(block, { x: e.clientX, y: e.clientY })) return;
                                    onEditBlock?.(block.id);
                                }}
                                title={canEditInline
                                    ? 'Drag to move · double-click to edit text here · Shift-click to multi-select'
                                    : 'Drag to move · double-click to edit · Shift-click to multi-select'}
                            />
                        )}
                        {isSelected && !isInlineEditing && !groupSelection && (
                            <div
                                className={`slide-designer__tab ${frame.y < 4 ? 'slide-designer__tab--inside' : ''}`}
                                role="button"
                                tabIndex={0}
                                data-keep-focus="1"
                                aria-label={`${tabLabel(block)} object selected. Press Delete or Backspace to remove it.`}
                                title={`${tabLabel(block)} · press Delete or Backspace to remove · drag to move · Esc to deselect`}
                                onPointerDown={(e) => {
                                    // Focus the tab first: startDrag suppresses the
                                    // browser's own focus so the drag stays smooth.
                                    e.currentTarget.focus({ preventScroll: true });
                                    startDrag(e, block, index, 'move');
                                }}
                            >
                                <span className="slide-designer__tab-grip" aria-hidden="true">⠿</span>
                                {tabLabel(block)}
                            </div>
                        )}
                        {isSoleSelection && !isInlineEditing && (
                            <>
                                {RESIZE_HANDLES.map((handle) => {
                                    const isCorner = handle.length === 2;
                                    const scalesText = isCorner && !SCALED_MEDIA_TYPES.has(block.type);
                                    return (
                                        <div
                                            key={handle}
                                            className={`slide-designer__handle slide-designer__handle--${handle}`}
                                            title={scalesText ? 'Drag corner to resize; text shrinks when the box gets smaller' : 'Drag to resize'}
                                            onPointerDown={(e) => startDrag(e, block, index, 'resize', handle)}
                                        />
                                    );
                                })}
                                {/* The toolbar normally floats above the object, but
                                    the stage clips at its own top edge and the slide
                                    header sits right above it — so an object near the
                                    top would have its controls cut off. Then it flips
                                    inside, the way the selection tab and the inline
                                    ribbon already do. */}
                                <div
                                    className={`slide-designer__toolbar ${frame.y < TOOLBAR_FLIP_Y ? 'slide-designer__toolbar--inside' : ''}`}
                                    onPointerDown={(e) => e.stopPropagation()}
                                >
                                    <button type="button" onClick={() => onEditBlock?.(block.id)} title="Edit content in the full editor modal">✏️</button>
                                    {canEditInline && (
                                        <button
                                            type="button"
                                            className="slide-designer__tooltext"
                                            onClick={() => beginInlineEdit(block)}
                                            title="Edit the text here on the slide (or double-click the block)"
                                        >
                                            T
                                        </button>
                                    )}
                                    {SCALED_MEDIA_TYPES.has(block.type) && (
                                        <button type="button" onClick={() => fitFrameToContent(block, index)} title="Fit frame to content">⛶</button>
                                    )}
                                    {!SCALED_MEDIA_TYPES.has(block.type) && (
                                        <span className="slide-designer__toolgroup">
                                            <button type="button" onClick={() => adjustTextScale(block, index, -0.1)} title="Decrease text size">A−</button>
                                            <button
                                                type="button"
                                                onClick={() => resetTextScale(block, index)}
                                                title="Reset text size to 100%"
                                                className="slide-designer__toolreadout"
                                            >
                                                {Math.round((frame.s || 1) * 100)}%
                                            </button>
                                            <button type="button" onClick={() => adjustTextScale(block, index, 0.1)} title="Increase text size">A+</button>
                                        </span>
                                    )}
                                    {canDetach && (
                                        <button type="button" onClick={() => detachCitation(block.id)} title="Detach the citation into its own movable object">🔖</button>
                                    )}
                                    {quoteCount > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => extractQuotes(block.id)}
                                            title={quoteCount === 1
                                                ? 'Pop the quote out into its own APA Quote block'
                                                : `Pop ${quoteCount} quotes out into their own APA Quote blocks`}
                                        >
                                            ❝{quoteCount > 1 ? quoteCount : ''}
                                        </button>
                                    )}
                                    <button type="button" onClick={() => changeDepth(block.id, 1)} title="Bring forward">⬆</button>
                                    <button type="button" onClick={() => changeDepth(block.id, -1)} title="Send backward">⬇</button>
                                    <button type="button" onClick={() => onDeleteBlock?.(block.id)} title="Delete block">🗑</button>
                                </div>
                            </>
                        )}
                    </div>
                );
            })}
            {marquee && (() => {
                const box = {
                    x: Math.min(marquee.origin.x, marquee.cur.x),
                    y: Math.min(marquee.origin.y, marquee.cur.y),
                    w: Math.abs(marquee.cur.x - marquee.origin.x),
                    h: Math.abs(marquee.cur.y - marquee.origin.y)
                };
                if (box.w < 0.4 && box.h < 0.4) return null;
                return (
                    <div
                        className="slide-designer__marquee"
                        style={{ left: `${box.x}%`, top: `${box.y}%`, width: `${box.w}%`, height: `${box.h}%` }}
                        aria-hidden="true"
                    />
                );
            })()}
            {isFileOver && (
                <div className="slide-designer__dropzone" aria-hidden="true">
                    <span>Drop image to add it to this slide</span>
                </div>
            )}
            {rulersActive && (
                <div className="slide-guides" aria-hidden="true">
                    {guides.v.map((pos, i) => (
                        (draggingExisting && draggingExisting.axis === 'v' && draggingExisting.index === i)
                            ? null
                            : (
                                <div
                                    key={`gv${i}`}
                                    className="slide-guide slide-guide--v"
                                    style={{ left: `${pos}%` }}
                                    onPointerDown={(e) => beginGuide(e, 'v', i)}
                                    onDoubleClick={(e) => { e.stopPropagation(); removeGuide('v', i); }}
                                    title="Drag to move · double-click (or drag off canvas) to delete"
                                />
                            )
                    ))}
                    {guides.h.map((pos, i) => (
                        (draggingExisting && draggingExisting.axis === 'h' && draggingExisting.index === i)
                            ? null
                            : (
                                <div
                                    key={`gh${i}`}
                                    className="slide-guide slide-guide--h"
                                    style={{ top: `${pos}%` }}
                                    onPointerDown={(e) => beginGuide(e, 'h', i)}
                                    onDoubleClick={(e) => { e.stopPropagation(); removeGuide('h', i); }}
                                    title="Drag to move · double-click (or drag off canvas) to delete"
                                />
                            )
                    ))}
                    {guideDrag && guideDrag.pos >= 0 && guideDrag.pos <= 100 && (
                        <div
                            className={`slide-guide slide-guide--${guideDrag.axis} slide-guide--live`}
                            style={guideDrag.axis === 'v'
                                ? { left: `${guideDrag.pos}%` }
                                : { top: `${guideDrag.pos}%` }}
                        >
                            <span className="slide-guide__readout">{Math.round(guideDrag.pos)}%</span>
                        </div>
                    )}
                </div>
            )}
        </div>
        </div>
        </div>
    );
};

export default SlideDesigner;
